// Libs
import findIndex from 'lodash/findIndex';
import * as Sentry from '@sentry/browser';
// Our stuff
import translations from '_shared_/lang/translations';
import localizations from '_shared_/lang/localizations.js';
import integrations from '_shared_/js/integrations/integrationsData.js';
import isDarkTheme from '_shared_/js/utils/isDarkTheme.js';
import { isAnnotationFromAnotherReader } from '_shared_/js/utils/annotationOwnership.js';
import plainTextToEditorHtml from '_shared_/js/tiptap/plainTextToEditorHtml.js';
import { findTocItemByPage } from '_shared_/js/utils/tableOfContents.js';
import { stepBack } from '_shared_/js/utils/backSteps';
import { dispatchAnimation } from '../../_shared_/js/utils/pageTransitionHelper';
import { aiIntegrationsAPI } from './communication/integrations';
import { DEFAULT_HIGHLIGHT_COLORS, MAX_CUSTOM_TAGS, MAX_TOTAL_TAGS } from '../../_shared_/js/constants/highlightColors';
import { DEFAULT_TOAST_DURATION_MS } from '../../_shared_/js/constants/toastDurations';
import { resolveSidebarLeftWidth } from '../../_shared_/js/constants/sidebarLayout';
// modules
import PdfPagedReaderScale from './store/modules/PdfPagedReaderScale';
import { isAndroid as detectAndroid, isApple as detectApple } from './utils/platform.js';

import { notesAPI } from './communication/notes';
import { bookmarksAPI } from './communication/bookmarks';
import bookmarkModel from './bookmarkModel.js';
import { flashcardsAPI } from './communication/flashcards';
import { hydrateFlashcardsFromHost, hydrateHighlightsFromHost } from './hostListHydration.js';
import { navigationAPI, settingsAPI, uiAPI } from './communication';
import { printAPI } from './communication/print';
import waitForHostJob from './waitForHostJob.js';
import { interactionEventsTracker, isPagebreakEmissionMode } from './communication/analytics';
import { Link } from '@readium/shared';
import {
    API_FONT_SIZE_BASE,
    API_FONT_SIZE_MAX,
    API_FONT_SIZE_MIN,
    FONT_SIZE_MAX,
    FONT_SIZE_MIN,
    createR2Locator,
} from './components/partials/Epub/Readium/readium2.js';
import {
    FONT_SIZE_DEFAULT,
    READER_HEADER_HEIGHT,
    SCROLL_SECTION_BUTTON_CLEARANCE,
} from './components/partials/Epub/Readium/readium2-styles.js';
import {
    createEmptyEpubTocLocation,
    epubTocLocationsEqual,
    getEpubTocItemAtPath,
    getEpubTocItemsAtPaths,
} from './components/partials/Epub/Readium/epubTableOfContents.js';
import {
    ADD_ON_EDITOR_CONFIRMED,
    addOnEditorHoldsPageTurn,
    addOnsActions,
    addOnsGetters,
    addOnsMutations,
} from './store/addOns.js';
import { addOnBatchActions, addOnBatchGetters, addOnBatchMutations } from './store/addOnBatch';
import { toursActions, toursMutations, toursState } from './store/tours';

// Environment variables
const CONIGLIO_URL = import.meta.env.VITE_CONIGLIO_URL;
const ENVIRONMENT = import.meta.env.VITE_ENV;
const VOLPE_HOST = import.meta.env.VITE_VOLPE_HOST;

const DEFAULT_USER_SETTINGS = () => ({
    epub: { layout: null, zoom: null, fontFamily: null, lineHeight: null, margins: null },
    pdf: { layout: null },
    theme: null,
    quickHighlight: { enabled: false, activeColorSlug: null },
});

// Guards toggleBookmark against re-entrancy: a rapid double-tap could otherwise create
// two bookmarks for the same page (the first isn't in `bookmarks` until store() resolves).
let bookmarkToggleInFlight = false;

// Guards toggleFullscreen against re-entrancy: rapid clicks all read the same
// `state.isFullscreen`, so they would resend the same desired state and let a stale
// response overwrite a newer one.
let fullscreenRequestInFlight = false;

/**
 * Builds an unsaved bookmarkModel anchored to the current reading location. PDF carries the
 * page; EPUB carries the Readium global position as `page` plus the serialized RWPM locator.
 * Returns null when the EPUB locator is not yet established, so the caller can abort.
 * @param {Object} state - Vuex state
 * @param {Object} getters - Vuex getters
 * @returns {Object|null} an unsaved bookmarkModel, or null if the location is unavailable
 */
export function buildBookmarkAtCurrentLocation(state, getters) {
    const bookmark = { ...bookmarkModel, id: null };

    // The page always comes from the reader's current page
    // (state.reader.pagination.current, exposed via the currentPage getter) for
    // both PDF and EPUB, so it stays in sync with what the reader reports.
    bookmark.page = getters.currentPage;

    if (getters.isEpub) {
        const locator = state.lastLocation.epub.locator;
        // Require progression (not position): matchBookmarkInViewport detects "current page"
        // by checking progression against the visible range, so a locator without progression
        // would create a bookmark the toggle could never recognize (and re-tapping would
        // duplicate it). progression === 0 is valid (start of a resource).
        if (!locator || locator.locations?.progression == null) {
            return null;
        }
        bookmark.type = 'epub';
        // EPUB also carries the full serialized RWPM locator (flattens cssSelector/fragments
        // into JSON) so navigation can restore the precise element via go()'s cssSelector cascade.
        bookmark.locator =
            typeof locator.serialize === 'function'
                ? locator.serialize()
                : {
                      href: locator.href,
                      type: locator.type,
                      locations: {
                          position: locator.locations.position,
                          progression: locator.locations.progression,
                          totalProgression: locator.locations.totalProgression,
                      },
                  };

        const anchor = state.epubViewer?.captureReadingAnchor?.();
        if (anchor?.href === bookmark.locator.href) {
            const serialized = anchor.serialize();
            bookmark.locator.locations = { ...bookmark.locator.locations, ...serialized.locations };
            bookmark.locator.text = serialized.text;
        }
    } else {
        bookmark.type = 'pdf';
    }

    return bookmark;
}

const BASE_THEMES = ['system', 'light', 'dark'];

// Scroll-mode bottom clearance (px) used until ReaderFooter measures its shown
// height, which then takes over so content clears the fixed footer.
const READER_BOTTOM_BAR_CLEARANCE_FALLBACK_PX = 96;

// Sidebar panel offset constants (panel width + gap)
const SIDEBAR_NO_OFFSET = 0;
const SIDEBAR_PANEL_OFFSET = 380;

// What a toast carries when the caller leaves a field out; a new toast starts
// from these, so it never inherits the one it replaces.
const TOAST_DEFAULTS = {
    text: '',
    durationMs: DEFAULT_TOAST_DURATION_MS,
    icon: null,
    progress: false,
    persistent: false,
    action: null,
};

/**
 * Tells the user a settings change was not saved, so it does not silently
 * revert on the next load, and reports the error to Sentry.
 * @param {Object} ctx - Vuex action context
 * @param {Error} error - The rejection from the settings save
 * @param {{ offerRetry?: boolean }} [options] - offerRetry adds a button that saves the current settings again,
 *   for callers that keep the unsaved change on screen
 */
function reportSettingsSaveFailure({ state, commit }, error, { offerRetry = false } = {}) {
    Sentry.captureException(error);
    const strings = state.localizations[state.lang];
    commit('openToast', {
        text: strings.settingsSaveFailed,
        ...(offerRetry && {
            action: { label: strings.settingsSaveRetry, dispatch: 'saveUserSettings', payload: {} },
        }),
    });
}

// Every save sends the full settings, so a later save that succeeds also stores an earlier one that failed.
let latestSettingsSave = 0;

/**
 * Fills a print copy's page range placeholders.
 * @param {string} template - Copy with `{from}` and `{to}`
 * @param {{ pageFrom: number, pageTo: number }} range
 * @returns {string}
 */
const withPrintRange = (template, { pageFrom, pageTo }) => template.replace('{from}', pageFrom).replace('{to}', pageTo);

/**
 * Pages in a printed range, both ends included.
 * @param {{ pageFrom: number, pageTo: number }} range
 * @returns {number}
 */
const printedPageCount = ({ pageFrom, pageTo }) => pageTo - pageFrom + 1;

/**
 * Reflects a printed range in the lifetime counter and history. A reprint from
 * the history (source: 'history') never consumes quota nor adds history.
 * @param {Function} commit
 * @param {{ source: 'new'|'history', pageFrom: number, pageTo: number }} request
 */
function countPrintedRange(commit, { source, pageFrom, pageTo }) {
    if (source !== 'new') {
        return;
    }
    commit('addPrintedPages', { pageFrom, pageTo });
    commit('addPrintHistoryEntry', { pageFrom, pageTo });
}

/**
 * @param {Function} commit
 * @param {Object} printStrings - The `print` localizations
 */
function openPrintRequestFailedToast(commit, printStrings) {
    commit('openToast', { text: printStrings.requestFailed, durationMs: 5000 });
}

const PRINT_TOAST_ICON = 'baseline-print-24px';

/**
 * Requests a print job from a host that lets the reader follow it, and keeps
 * the reader posted until the PDF is ready or the job failed. The "preparing"
 * notice stays up with a wait bar, and closing it does not stop the wait: the
 * "ready" notice still shows, with a Print link that opens the PDF in a new
 * tab, and a failed job offers to try again. The quota, the history and the
 * print-create event take the range only once the PDF is ready, since that is
 * when the host counts it: a job that fails and is retried counts once.
 *
 * @param {Object} ctx - Vuex action context
 * @param {{ fileType: 'pdf'|'epub', pageFrom: number, pageTo: number, source: 'new'|'history' }} payload
 * @returns {Promise<import('./communication/print/PrintAPI').PrintPagesResult|null>} The job, or null when no PDF came of it
 */
async function requestPrintPagesInReader({ state, commit }, payload) {
    const printStrings = state.localizations[state.lang].print;

    commit('openToast', {
        text: withPrintRange(printStrings.readerDelivery.preparing, payload),
        icon: PRINT_TOAST_ICON,
        progress: true,
        persistent: true,
    });

    let result;
    let job;
    try {
        result = await printAPI.requestPrintPages(payload);
        job = await waitForHostJob(() => printAPI.getPrintJob({ jobId: result.jobId }));
    } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        openPrintRequestFailedToast(commit, printStrings);
        return null;
    }

    if (job === null) {
        Sentry.captureMessage(`Print job ${result.jobId} did not settle in time`, 'warning');
        openPrintRequestFailedToast(commit, printStrings);
        return null;
    }

    if (job.status === 'failed') {
        commit('openToast', {
            text: withPrintRange(printStrings.readerDelivery.failed, payload),
            icon: PRINT_TOAST_ICON,
            persistent: true,
            action: { label: printStrings.readerDelivery.retry, dispatch: 'requestPrintPages', payload },
        });
        return null;
    }

    commit('openToast', {
        text: withPrintRange(printStrings.readerDelivery.ready, payload),
        icon: PRINT_TOAST_ICON,
        persistent: true,
        action: { label: printStrings.action, href: job.url },
    });
    interactionEventsTracker.trackPrint(result.jobId, printedPageCount(payload));
    countPrintedRange(commit, payload);
    return result;
}

// Runtime validation for VOLPE_HOST
if (!VOLPE_HOST) {
    console.error('CRITICAL: VITE_VOLPE_HOST environment variable is not set!');
    throw new Error('VITE_VOLPE_HOST environment variable is required. This should be set during build time.');
}

if (!['farfalla', 'fenice'].includes(VOLPE_HOST)) {
    console.error(`CRITICAL: Invalid VITE_VOLPE_HOST value: "${VOLPE_HOST}"`);
    throw new Error(`Invalid VITE_VOLPE_HOST value: "${VOLPE_HOST}". Must be either "farfalla" or "fenice".`);
}

export default {
    // strict: process.env.VITE_ENV !== 'production',
    state: {
        ...toursState,
        volpeHost: VOLPE_HOST,
        plaPublicationManifestVersion: null,
        tenantId: null, // Tenant ID for X-Farfalla-Tenant-Id header
        contentEncryption: {
            ivSeed: {
                tenant_id: null,
                issue_id: null,
            },
        },
        coniglio: {
            url: CONIGLIO_URL,
            track_token: '',
        },
        // fileType: 'audio',
        fileType: 'pdf',
        // fileType: 'epub',
        embeddedMode: false,
        previewMode: false,
        customPreview: false,
        disableNext: false,
        environment: ENVIRONMENT,
        inIframe: false,
        embeddedInMobileApp: VOLPE_HOST === 'fenice',
        isLoadingProcessFinished: false,
        sessionExpired: false,
        polpoSessionInitiated: false,
        readerToken: null,
        anonymousSession: false,
        parentUrl: null,
        // English is the eagerly-bundled fallback and the only language present
        // at boot. The tenant's real language is loaded on demand and committed
        // by the LoadingProcess gate before the UI needs it.
        lang: 'en',
        translations,
        localizations,
        title: '',
        isLoading: true,
        isInitialContentReady: false,
        loadingSpinnerPosition: null,
        uiHidden: false,
        /**
         * Mirrors the fullscreen state the host reports. Never set optimistically on click.
         */
        isFullscreen: false,
        device: {
            width: 0,
            height: 0,
            orientation: 'portrait',
        },
        isBuyable: false,
        userIsGuest: false,
        isBuyableButtonText: '',
        exhibitionMode: {
            enabled: false,
            label: '',
        },

        // check if merge with `publication` obj
        issue: {
            title: '',
            tags: '',
            issue_url: '',
            lang: 'en',
            share_url: '',
            publication_date: '',
            publicationPlace: '',
            aboutThisPublication: '',
            descriptionRawText: '',
            externalId: '',
            twitterUser: '',
            // EPUB carries printed-page markers (host-provided). Drives the
            // page-change emission branch in TrackingEventsListener.
            hasPagebreaks: false,
        },

        // check if delete
        sidebar: {
            opened: false,
        },

        reader: {
            layout: 'scroll',

            pagination: {
                ready: false,
                total: 1,
                current: 1,

                isFirstPage: false,
                isLastPage: false,
            },

            // check if delete
            data: {
                toc: [],
            },

            keyboardNavigationEnabled: true,
            keyboardNavigationHolders: [],
            pageViewConfirmDelayMs: null,
            socialShareDisabled: false,

            // uuid of the bookmark anchored within the currently visible page, or null.
            // Recomputed imperatively on page/position change (see refreshCurrentPageBookmark)
            // because the EPUB visible range lives on the non-reactive navigator instance and
            // cannot be tracked by a reactive getter.
            currentPageBookmarkUuid: null,
        },
        // `?add_on_id=`, until it is answered. Cleared then, so the link is
        // a question the session asks once.
        sharedArticle: {
            id: '',
        },
        // The panel the host asks the reader to open with, until it is answered.
        initialPanel: null,
        search: {
            requestQueryWordsHighlight: false,
            queryWords: [],
            clear: false,
        },
        // check merge into one object
        articles: [],

        // check if merge with `issue` obj
        publication: {
            cover: null,
            table_of_contents: [],
            files_urls: [],
            files_info: [],
            url: null,
        },

        audiobook: {
            playlist: [],
            chapters: null,
            duration: null,
            currentTrackDurationInSeconds: null,
            speed: 1,
            track: {
                durationInSeconds: null,
            },
            trackPlayer: {
                isLoading: false,
                isPlaying: false,
            },
        },

        ui: {
            theme: 'system',
            baseTheme: 'system',
            themeSelected: 'light',
            themeOptions: [
                {
                    value: 'system',
                    icon: 'theme-system',
                    solidIcon: 'theme-system',
                    label: 'System',
                },
                { value: 'light', icon: 'sun', solidIcon: 'sun-solid', label: 'Light' },
                { value: 'dark', icon: 'moon', solidIcon: 'moon-solid', label: 'Dark' },
            ],
            headerbar: {
                offsetHeight: 0,
            },
            toolbar: {
                // Live footer height (collapses when the UI hides). Floating
                // buttons position against this.
                offsetHeight: 0,
                // Footer height captured while the UI is shown. Feeds the bottom
                // clearance, staying stable when the footer collapses on toggle.
                shownHeight: 0,
            },
            thumbnails: {
                opened: false,
                enabled: true,
            },
            pagination: {
                current: 1,
            },
            sidebar: {
                opened: false,
                /**
                 * User-resized width (px) for the left panel; null falls back to the
                 * default `resolveSidebarLeftWidth` derives from the reader's width.
                 */
                leftWidth: null,
                /**
                 * True while the user is actively dragging the resize handle. Lets the EPUB
                 * viewer skip its per-frame reflow and track the pointer instantly.
                 */
                resizing: false,
            },
            features: {
                highlights: {
                    opened: false,
                },
                notes: {
                    opened: false,
                },
                bookmarks: {
                    opened: false,
                    enabled: false,
                },
                flashcards: {
                    enabled: false,
                },
                // Mirrors the host's addon_rich_text_editor tenant flag, false
                // when the host does not send it. It governs the note
                // editor alone: a note stores a plain column beside
                // its markup and can fall back to the textarea.
                // An add-on body cannot.
                richTextEditor: {
                    enabled: false,
                    // Embed provider hostnames the editor may offer paste-to-embed
                    // from (subdomains match implicitly): all for add-ons, video
                    // and audio for notes. Served by the host; save-time
                    // enforcement stays server-side. Audio is a list of its own
                    // because its player is sized differently, not because the
                    // editor treats it as a lesser embed.
                    embedHosts: {
                        all: [],
                        video: [],
                        audio: [],
                    },
                },
                fullscreen: {
                    enabled: false,
                },
                addOns: {
                    editing: false,
                    batch: false,
                },
            },
            search: {
                opened: false,
                enabled: false,
            },
            notebook: {
                opened: false,
            },
            flashcardsPanel: {
                opened: false,
                // The card form over the list: `card` is the one being edited, or null for a new one.
                form: null,
            },
            addOnsPanel: {
                opened: false,
                // The piece being read; null shows the list.
                addOnId: null,
                // The piece a list row is pointing at.
                highlightedId: null,
                fullscreen: false,
                // What the reader typed over the list; narrows the list and the neighbours.
                search: '',
                // Session-only: the panel forgets the zoom it was left at on close.
                fontScale: 1,
                // The form being filled. Null shows the viewer or the list.
                editor: null,
            },
            // Outside addOnsPanel, so closing or resetting the panel never touches a batch on its way.
            addOnBatch: {
                dialogOpened: false,
                // The batch being created, then its outcome until dismissed.
                run: null,
            },
            flashcardModal: {
                opened: false,
            },
            tableOfContents: {
                opened: false,
                enabled: true,
            },
            listening: {
                opened: false,
                enabled: true,
                texToListen: '',
                pageToListen: '',
                uuidToListen: '',
                driver: 'native-tts',
            },
            extraVoices: {
                chinese: false,
            },
            selectionTool: {
                enabled: true,
                opened: false,
                position: {},
                selectionTop: 0,
                selectionLeft: 0,
            },
            rightSubmenu: {
                opened: false,
            },
            citationModal: {
                opened: false,
            },
            printModal: {
                opened: false,
            },
            plugin: {
                opened: false,
                enabled: true,
                // Key of the plugin whose panel `opened` refers to.
                activeKey: null,
                plugins: [],
            },
            settings: {
                opened: false,
                enabled: true,
                zoom: {
                    enabled: true,
                    select: {
                        enabled: true,
                        types: ['page-fit', 'page-width'],
                        isSelected: 'page-fit',
                        scale: 1,
                    },
                },
            },
            paginator: {
                enabled: true,
            },
            audiobook: {
                about: {
                    opened: false,
                },
                playlist: {
                    opened: false,
                },
            },
            socialShare: {
                opened: false,
                shareArticle: false,
                currentArticleId: null,
            },
            shareCollectionModal: {
                opened: false,
            },
            /**
             * The note editor lives in the notebook, not in a modal of its own:
             * `uuid` is the note being edited in place, or null while a new one is
             * written against the pending selection in `currentHighlight`.
             */
            noteEditor: {
                opened: false,
                uuid: null,
            },
            confirmationModal: {
                opened: false,
                title: '',
                message: '',
                confirmLabel: '',
                cancelLabel: '',
                onConfirmAction: null,
                context: {},
                // A confirmation deletes something unless its caller says otherwise, as its default label does.
                destructive: true,
            },
        },
        systemThemeMediaQuery: null,
        handleSystemThemeChange: null,
        concurrencyLimit: {
            enforce: false,
            debug: false,
        },

        aiActions: null,
        integrations,
        integrationTool: {
            opened: false,
            name: null,
            text: '',
            action: '',
        },

        AiIntegrations: {
            enabled: false,
            text: '',
            action: '',
        },

        copyContentToClipboard: {
            enabled: true,
            preventCopyToClipboardEvent: true,
        },

        // Controlled print (FUL-172/FUL-291). Limits and per-user state arrive from
        // the host (hardcoded in backend for now); the watermarked PDF is generated
        // backend-side and is out of scope here.
        printControl: {
            enabled: false,
            printLimitPerJob: 0, // drm_print_limit (max pages per job)
            lifetimeTotalPages: 0, // Y = drm_lt_print_percentage × total book pages
            lifetimePercentage: 0, // drm_lt_print_percentage (Z% of the book)
            printedPages: [], // unique pages already printed by this user in this book
            history: [], // [{ pageFrom, pageTo }] — printed ranges; reprint re-requests the same range
            readerDelivery: false, // the host lets the reader follow the job and open its PDF, instead of only emailing it
        },

        toast: { opened: false, ...TOAST_DEFAULTS },
        onboarding: {
            hasToBeShown: false,
        },
        tourBanner: {
            opened: false,
        },

        promoteApp: {
            enabled: false,
            hasToBeShown: false,
            androidAppLink: '',
            appleAppLink: '',
            appName: '',
            appIcon: '',
        },

        // check merge into one object
        highlightModel: {},
        currentHighlight: {},
        selectionsHaveBeenFetched: false,
        selections: [],
        bookmarks: [],
        // Whether `bookmarks` above is a settled answer from the host rather than the empty
        // array it starts as. Stays false for sessions that never fetch (anonymous, feature
        // off), which is what keeps toggleBookmark inert for them.
        bookmarksHaveBeenFetched: false,

        flashcardModel: {},
        /**
         * Flashcards created by the user, loaded via flashcardsAPI.
         * Each is a flashcardModel instance: { id, uuid, frontText, backText, color, colorSlug, sourceText?, location?, ... }
         */
        flashcards: [],
        flashcardsHaveBeenFetched: false,
        /**
         * uuid of the flashcard the panel should scroll to, set when the user clicks a flashcard
         * underline in the reader. Cleared by ReaderFlashcards after it scrolls. Null when idle.
         */
        flashcardFocusUuid: null,

        /**
         * uuid of the note the notebook should scroll to, set when the reader taps a note
         * written by another reader. Cleared by ReaderNotes after it scrolls. Null when idle.
         */
        noteFocusUuid: null,

        /**
         * Full-screen study session state. `cards` is the working set being studied,
         * `currentIndex` points at the visible card, and `isFullscreen` toggles the
         * default vs. fullscreen layout. `ratings` holds the per-session self-assessment
         * keyed by card id (`'know' | 'dontKnow'`); it is never persisted to the backend.
         */
        flashcardStudyMode: {
            active: false,
            cards: [],
            currentIndex: 0,
            isFlipped: false,
            isFullscreen: false,
            ratings: {},
        },

        /**
         * User-defined custom colors loaded from Reader Settings.
         * Each color has: { slug, label, color }
         */
        customColors: [],

        /**
         * Colors from subscribed authors' shared tags.
         * Each color has: { slug, label, color, author: { uuid, name } }
         */
        sharedColors: [],

        /**
         * Quick highlight mode: auto-applies the active color on text selection.
         * activeColorSlug references a color from the allHighlighters getter.
         */
        quickHighlight: {
            enabled: false,
            activeColorSlug: null,
        },
        quickHighlightToggleSaveInFlight: false,

        /**
         * From Scimia
         */
        isFixedLayout: false,
        fixedLayoutRulesOutSpreads: false,
        lastLocation: {
            epub: {
                chapterHref: '',
                cfi: '',
                locator: null, // R2 Locator object
            },
            audio: {
                currentSeek: 0,
                currentTrack: 0,
            },
        },
        locationBeforeFontChange: {
            chapterHref: '',
            cfi: '',
        },
        settingsChange: false,
        doNotTrackFontLoc: false,
        hasLoadLastLocation: false,
        context: [],
        settings: {
            fontSize: 1, // REM value (1 = 100%, 0.75 = 75%, 4 = 400%)
            scroll: 'auto', // auto | scroll-continuous
            syntheticSpread: 'single', // double | single
        },
        readium: null,
        epubViewer: null,
        epub: {
            direction: 'ltr',
            farfallaToc: [],
            currentTocLocation: createEmptyEpubTocLocation(),
            filePath: null, // Set dynamically from testDataLoader in dev mode
            spine: [],
            totalWords: 0,
            subSpine: {},
            totalPositions: 0, // R2: total number of positions from EpubNavigator
            currentPosition: 1, // R2: current position; drives the progress-bar thumb + navigation
            // Printed pages of the current fixed-layout spread (ascending),
            // fed by the viewer from the spread's own markers. FXL only: the
            // pagebreak tracker never observes FXL frames, so the readout
            // page cannot answer there. Consumed by the reader plugin.
            fixedLayoutPrintedPages: [],
        },
        userSettings: DEFAULT_USER_SETTINGS(),
        isOffline: false,
        epubLayouts: {
            single: {
                scroll: 'auto',
                syntheticSpread: 'single',
            },
            scroll: {
                scroll: 'scroll-doc',
                syntheticSpread: 'auto',
            },
            double: {
                scroll: 'auto',
                syntheticSpread: 'auto',
            },
        },
        addToLists: {
            show: false,
            iframeUrl: '',
        },
        selectionInProgress: false,
    },
    getters: {
        isEmbeddedMode({ embeddedMode }) {
            return embeddedMode;
        },
        /**
         * True when we're offline against remotely-served content — the only case where the
         * locally-backed features (notes/highlights, flashcards, bookmarks) must stay disabled.
         * In Fenice the content is downloaded and the host keeps that data on-device, so they
         * keep working offline; `embeddedInMobileApp` stands in for "content is local". On web
         * (Farfalla) the content is streamed, so offline means no host data at all. Search keeps
         * the plain `isOffline` gate everywhere — it is a live host query, not stored data.
         */
        isOfflineOnRemoteContent(state) {
            return state.isOffline && !state.embeddedInMobileApp;
        },
        /**
         * Top clearance (px) so content clears the fixed header in scroll
         * mode. Inherits the header's measured height, falling back to a
         * default until the header is measured.
         */
        readerTopBarClearance(state) {
            return state.ui.headerbar.offsetHeight || READER_HEADER_HEIGHT;
        },
        /**
         * Bottom clearance (px) so content clears the fixed footer in scroll
         * mode. Inherits the footer's shown height, falling back to a default
         * until the footer is measured.
         */
        readerBottomBarClearance(state) {
            return state.ui.toolbar.shownHeight || READER_BOTTOM_BAR_CLEARANCE_FALLBACK_PX;
        },
        epubScrollClearances(_state, getters) {
            return {
                top: getters.readerTopBarClearance + SCROLL_SECTION_BUTTON_CLEARANCE,
                bottom: getters.readerBottomBarClearance + SCROLL_SECTION_BUTTON_CLEARANCE,
            };
        },
        canUseNativeShare(state, getters) {
            /**
             * In fenice or any mobile app we'll always use the OS native share dialog.
             * In smartphones we'll try to use the OS native share dialog.
             * Desktop browsers do not support the OS native share dialog, except for Safari in macOS. But
             * the experience is not good enough so we fallback to our custom UI.
             */
            return (state.embeddedInMobileApp || navigator.share) && !getters.isDesktopBrowser;
        },
        socialShareDisabled(state) {
            return state.reader.socialShareDisabled;
        },
        isDesktopBrowser(_getters) {
            const userAgent = navigator.userAgent;

            // Detect if it's an iOS device (iPod, iPhone, or iPad)
            const isIOS = Boolean(userAgent.match(/(iPod|iPhone|iPad)/));

            // Detect if it's using WebKit, common in Safari and other browsers on iOS
            const isAppleWebKit = Boolean(userAgent.match(/AppleWebKit/));

            // If it's an iOS device, it's not a desktop browser
            if (isIOS && isAppleWebKit) {
                return false;
            }

            // Detect desktop Safari (excluding Chrome and Android)
            const isSafari = Boolean(/^((?!chrome|android).)*safari/i.test(userAgent));

            // Detect desktop Chrome (excluding Android)
            const isChrome = Boolean(userAgent.match(/Chrome\/[0-9]+/) && !userAgent.match(/Mobile|Android/));

            // Return true if it's either Safari or Chrome on desktop
            return isSafari || isChrome;
        },
        isFeniceDesktopApp(_getters) {
            return Boolean(navigator.userAgent.match(/Electron/));
        },
        isShareEnabled(_state, _getters) {
            // Change this getter to hide the share button
            return true;
        },
        /**
         * The print control is exposed only when the feature is enabled and every
         * DRM limit is present and greater than zero (AC: ingesta y activación de la
         * feature). `lifetimeTotalPages` is required too: a zero total means the book
         * page count is unknown, which would leave the modal in a "quota exhausted"
         * dead-end (printRemainingPages === 0), so the control stays hidden instead.
         */
        isPrintControlEnabled(state) {
            return (
                state.printControl.enabled &&
                state.printControl.printLimitPerJob > 0 &&
                state.printControl.lifetimePercentage > 0 &&
                state.printControl.lifetimeTotalPages > 0
            );
        },
        /** Unique pages already printed by the user in this book. */
        printUsedPages(state) {
            return state.printControl.printedPages.length;
        },
        /** Remaining lifetime pages, never negative. */
        printRemainingPages(state, getters) {
            return Math.max(0, state.printControl.lifetimeTotalPages - getters.printUsedPages);
        },
        /** Whether the user has exhausted the accumulated lifetime quota. */
        isLifetimeQuotaExhausted(_state, getters) {
            return getters.printRemainingPages === 0;
        },
        isPreviewMode({ previewMode }) {
            return previewMode;
        },
        isCustomPreview({ customPreview }) {
            return customPreview;
        },
        showBuyOrLoginButton({ isBuyable, userIsGuest, previewMode }) {
            // Show the button if the publication is buyable or if the user is guest and the preview mode is enabled
            // When the publication is free, the button is not shown
            return isBuyable || (userIsGuest && previewMode);
        },
        isEpub({ fileType }) {
            return fileType === 'epub';
        },
        isPdf({ fileType }) {
            return fileType === 'pdf';
        },
        isAudiobook({ fileType }) {
            return fileType === 'audio';
        },
        /**
         * Is there text to read, annotate and navigate?
         *
         * Gates every text tool (notes, bookmarks, flashcards, page turning). A
         * format answers yes only once it has a viewer that renders text, so a
         * new medium inherits none of them.
         */
        hasTextContent(state, { isPdf, isEpub }) {
            return isPdf || isEpub;
        },
        /**
         * Which content can be addressed by page at all: any PDF, or an EPUB
         * with pages. A fixed-layout spine item is a page. A reflowable
         * book needs the host's page markers, and an unmarked one has
         * only Readium positions, which move with the font size.
         */
        hasPaginatedContent(state, { isPdf, isEpub }) {
            return isPdf || (isEpub && (state.issue.hasPagebreaks || state.isFixedLayout));
        },
        /**
         * Does this publication play on a timeline the reader starts, pauses
         * and seeks?
         *
         * Gates the transport chrome, which belongs to playback rather than to
         * a single medium. Audio is the only format that answers yes.
         */
        isPlayableMedia(state, { isAudiobook }) {
            return isAudiobook;
        },
        /**
         * Is this a format this build has no viewer for?
         *
         * Every viewer answers one of the two format questions above, so
         * a format that answers neither has nothing to render. Gates the
         * unsupported screen and the onboarding tour, which holds for
         * content this build renders.
         */
        isUnsupportedFormat(state, { hasTextContent, isPlayableMedia }) {
            return !hasTextContent && !isPlayableMedia;
        },
        /** The saved setting stays for the next book. */
        effectiveEpubLayout(state) {
            return state.fixedLayoutRulesOutSpreads ? state.epubLayouts.single : state.settings;
        },
        singlePageMode(state, { isEpub, effectiveEpubLayout }) {
            if (isEpub) {
                return effectiveEpubLayout.scroll === 'auto' && effectiveEpubLayout.syntheticSpread === 'single';
            }
            return state.reader.layout === 'single';
        },
        doublePageMode(state, { isEpub, effectiveEpubLayout }) {
            if (isEpub) {
                return effectiveEpubLayout.scroll === 'auto' && effectiveEpubLayout.syntheticSpread === 'auto';
            }
            return state.reader.layout === 'double';
        },
        scrollPageMode(state, { isEpub, isPreviewMode, effectiveEpubLayout }) {
            // Prevent scroll mode in EPUB previews
            if (isEpub && isPreviewMode) {
                return false;
            }

            if (isEpub) {
                return effectiveEpubLayout.scroll !== 'auto';
            }

            return state.reader.layout === 'scroll';
        },
        publicationNumberOfPages(state, { isEpub }) {
            // For EPUB: prefer the printed-page total; fall back to Readium
            // positions, then spine length, so the slider always has a max.
            if (isEpub) {
                return state.reader.pagination.total || state.epub.totalPositions || state.epub.spine.length;
            }
            // For PDF: using files_urls instead of files_info because files_info has all the pages, ignoring the preview mode
            return state.publication.files_urls.length;
        },
        /**
         * The EPUB progress readout as a `{ current, total }` pair on ONE scale, or null when
         * the book offers neither scale (nothing is rendered then).
         *
         * The two numbers used to be picked independently: the total came from
         * `reader.pagination.total`, committed for every EPUB, while `EpubViewer` chose the
         * numerator with `isPagebreakEmissionMode`. Where those disagree the readout mixed
         * units — a fixed-layout book rendered its Readium position over a printed-page total,
         * two unrelated scales with nothing marking the change. Reading both from the same
         * branch is the whole point: whichever scale answers, answers for both.
         *
         * Printed pages need the same predicate the viewer commits `currentPage` with, so it is
         * shared rather than restated here. The viewer reads the layout off the navigator
         * because the store flag lands after its first position event; the readout can use the
         * store flag directly, since it shows a spinner until `isLoading` clears and by then
         * the flag is committed.
         *
         * Positions are the fallback rather than a percentage: the reader speaks in whole
         * locations, and `ComponentRangePaginator` already renders this exact pair, so the bar
         * and the text stay on one scale. The fallback is meant to shrink to nothing — every
         * EPUB reaching it is a book the host did not mark (see the Sentry capture in
         * `storeEpubContent`), and printed pages are what bookmarks and print need.
         */
        epubProgressReadout(state, getters) {
            if (getters.epubUsesPrintedPages && state.reader.pagination.total > 0) {
                return { current: state.reader.pagination.current, total: state.reader.pagination.total };
            }
            if (state.epub.totalPositions > 0) {
                return { current: state.epub.currentPosition, total: state.epub.totalPositions };
            }
            return null;
        },
        /**
         * Whether the book counts in printed pages (pagebreak markers) rather
         * than Readium positions — the scale a bare page number is read on.
         */
        epubUsesPrintedPages(state, getters) {
            return isPagebreakEmissionMode({
                isEpub: getters.isEpub,
                hasPagebreaks: state.issue.hasPagebreaks,
                isFixedLayout: state.isFixedLayout,
            });
        },
        currentPage(state) {
            return state.reader.pagination.current;
        },
        /**
         * Current Readium position (1-based). Drives the EPUB progress-bar thumb
         * and its navigation, kept separate from the printed page shown in the
         * readout: the bar moves by reading progress (positions), the text reads
         * the printed page. Navigating by position hits a real Readium locator
         * (positions[i]) instead of proportionally guessing one from the page.
         */
        epubCurrentPosition(state) {
            return state.epub.currentPosition;
        },
        /**
         * Returns the current reading location based on file type.
         * For EPUB files, returns the EPUB location object; for other formats, returns the page number.
         * @returns {Object|number}
         */
        currentLocation(state, { isEpub, currentPage }) {
            return isEpub ? state.lastLocation.epub : currentPage;
        },
        currentPageIsFirstPage(state, { isEpub, currentPage }) {
            if (isEpub) {
                return state.reader.pagination.isFirstPage;
            }
            return currentPage === 1;
        },
        currentPageIsLastPage(state, { isEpub, currentPage, publicationNumberOfPages }) {
            if (isEpub) {
                return state.reader.pagination.isLastPage;
            }
            return currentPage === publicationNumberOfPages;
        },
        /**
         * In doublePageMode `state.ui.pagination.current` is the first
         * if the two pages being displayed
         */
        currentPages(
            { reader: _reader },
            { currentPage, doublePageMode, currentPageIsFirstPage, currentPageIsLastPage, publicationNumberOfPages }
        ) {
            if (!doublePageMode) {
                return [currentPage];
            }
            // In doublePageMode the first page is displayed individually
            if (currentPageIsFirstPage) {
                return [currentPage];
            }
            // In doublePageMode the last page might be displayed individually
            if (publicationNumberOfPages % 2 === 0 && currentPageIsLastPage) {
                return [currentPage];
            }
            return [currentPage, currentPage + 1];
        },
        hasTableOfContents(state) {
            return Array.isArray(state.publication.table_of_contents) && state.publication.table_of_contents.length > 0;
        },
        /**
         * Table of contents entry containing the current reading position.
         *
         * EPUB resolves a path in the effective TOC. PDF matches the last
         * visible page, so a chapter opening on the right-hand page of a
         * double-page spread counts as current. Audiobooks resolve to null.
         */
        currentTocItem(state, { isEpub, isPdf, currentPages }) {
            const toc = state.publication.table_of_contents;

            if (isEpub) {
                return getEpubTocItemAtPath(toc, state.epub.currentTocLocation.path);
            }

            if (isPdf) {
                return findTocItemByPage(toc, currentPages[currentPages.length - 1]);
            }

            return null;
        },
        currentTocAncestors(state, { isEpub }) {
            if (!isEpub) return [];

            return getEpubTocItemsAtPaths(
                state.publication.table_of_contents,
                state.epub.currentTocLocation.ancestorPaths
            );
        },
        currentTocConfidence(state, { isEpub, isPdf, currentTocItem }) {
            if (!currentTocItem) return 'none';
            if (isEpub) return state.epub.currentTocLocation.confidence;
            if (isPdf) return 'exact';

            return 'none';
        },
        hasFeatureActive(state) {
            return (
                state.ui.notebook.opened ||
                state.ui.thumbnails.opened ||
                state.ui.tableOfContents.opened ||
                state.ui.search.opened ||
                state.ui.plugin.opened
            );
        },
        onlyDesktopVisible(state, getters) {
            // Assumes that if the device is not mobile (based on the mobile visibility criteria), it is considered desktop
            return !getters.onlyMobileVisible;
        },
        onlyMobileVisible(state) {
            // This evaluates the real device width
            return Math.min(state.device.width, state.device.height) <= 570;
        },
        showHighlightsAndNotes(state, _getters) {
            return (
                !state.ui.features.notes.opened &&
                !state.ui.features.highlights.opened &&
                !state.ui.features.bookmarks.opened
            );
        },
        /**
         * Page-level bookmarks, ordered ascending by page. `page` is a PDF page number or an
         * EPUB printed page (fallback: Readium position). A single issue is always mono-format
         * (PDF *or* EPUB, never both), so the scales never mix within one list and this ordering
         * is well-defined.
         */
        bookmarks(state) {
            return state.bookmarks.slice().sort((a, b) => (a.page ?? 0) - (b.page ?? 0));
        },
        /**
         * The bookmark anchored within the currently visible page, resolved from the uuid the
         * refreshCurrentPageBookmark action commits (per-format match lives there).
         */
        currentPageBookmark(state) {
            const uuid = state.reader.currentPageBookmarkUuid;
            if (!uuid) return null;
            return state.bookmarks.find(bookmark => bookmark.uuid === uuid) ?? null;
        },
        isCurrentPageBookmarked(state, getters) {
            return Boolean(getters.currentPageBookmark);
        },
        canUseNotes(state, getters) {
            return getters.hasTextContent;
        },
        canUseBookmarks(state, getters) {
            // Gated by the host feature flag (state.ui.features.bookmarks.enabled, set from
            // initData.features.bookmarks) so it can be toggled per-config from the backend,
            // and only ever available where there is text to bookmark.
            //
            // The flag is per-tenant while anonymousSession is per-user, so the two are
            // independent: without this guard an anonymous reader on a bookmarks-enabled
            // tenant gets a toggle that fetchBookmarks refuses to populate. Gating here
            // rather than at each call site closes the header button, the notebook tab and
            // the fold at once, mirroring ComponentListsButton/QuickHighlightSection.
            return getters.hasTextContent && state.ui.features.bookmarks.enabled && !state.anonymousSession;
        },
        richTextEditorIsEnabled(state) {
            return state.ui.features.richTextEditor.enabled;
        },
        richTextEmbedHosts(state) {
            return state.ui.features.richTextEditor.embedHosts;
        },
        canUseFlashcards(state, getters) {
            return getters.hasTextContent && state.ui.features.flashcards.enabled && !state.anonymousSession;
        },
        /**
         * Embedded mode is excluded: the reader is a widget inside another page's layout there.
         */
        canUseFullscreen(state, getters) {
            return state.ui.features.fullscreen.enabled && !getters.isEmbeddedMode;
        },
        ...addOnsGetters,
        ...addOnBatchGetters,
        /**
         * Plugins address content by page ranges through the host's text
         * index, so the space only opens where that index can answer: any
         * PDF, or an EPUB whose printed-page pagination both exists
         * (`pagination.total`, host-provided via the content structure; 0
         * also covers hosts that predate the field) and is readable here —
         * marker-bearing reflowable books read it through the pagebreak
         * tracker, fixed-layout through the spread's markers (see
         * `pluginCurrentPages`). Audiobooks never qualify.
         */
        canUseReaderPlugins(state, getters) {
            const addressable = getters.hasPaginatedContent && (getters.isPdf || state.reader.pagination.total > 0);

            return addressable && state.ui.plugin.enabled && state.ui.plugin.plugins.length > 0;
        },
        /**
         * The plugins the reader offers, each with the name and icon its
         * button and panel show. Vito, and a plugin from a host that predates
         * the plugin list, keep «Habla con este libro» with the chat icon; an
         * external plugin shows the name its admin registered with a generic
         * plugin icon.
         */
        shownReaderPlugins(state) {
            return state.ui.plugin.plugins.map(plugin =>
                plugin.id === 'external'
                    ? { ...plugin, title: plugin.name, icon: 'puzzle-piece-24px' }
                    : { ...plugin, title: state.localizations[state.lang].chat.title, icon: 'chat-24px' }
            );
        },
        isReaderPluginOpen(state) {
            return key => state.ui.plugin.opened && state.ui.plugin.activeKey === key;
        },
        /**
         * Pages the reader plugin receives as the current reading point, in
         * the page domain its range requests are answered in (the host's
         * text index) — for EPUB always printed pages, never R2 positions.
         * Reflowable books read the tracker-maintained readout page;
         * fixed-layout spreads carry their own feed (empty until the first
         * spread resolves, which also delays the plugin's init handshake —
         * see ReaderSidebarPlugin).
         */
        pluginCurrentPages(state, getters) {
            if (!getters.isEpub) {
                return getters.currentPages;
            }
            if (state.isFixedLayout) {
                return state.epub.fixedLayoutPrintedPages;
            }

            return [state.reader.pagination.current];
        },
        /** Page total in the same domain as pluginCurrentPages. */
        pluginTotalPages(state, getters) {
            return getters.isEpub ? state.reader.pagination.total : getters.publicationNumberOfPages;
        },
        /**
         * Calculates the content offset when a persistent sidebar panel is open on desktop.
         * @param {Object} state - Vuex state
         * @param {Object} getters - Vuex getters
         * @returns {{ left: number, right: number }} Offset in pixels for each side
         */
        sidebarContentOffset(state, getters) {
            if (getters.onlyMobileVisible) {
                return { left: SIDEBAR_NO_OFFSET, right: SIDEBAR_NO_OFFSET };
            }

            const leftPanelOffset = resolveSidebarLeftWidth({
                leftWidth: state.ui.sidebar.leftWidth,
                viewportWidth: state.device.width || window.innerWidth,
            });

            const left =
                state.ui.tableOfContents.opened ||
                state.ui.notebook.opened ||
                state.ui.flashcardsPanel.opened ||
                state.ui.addOnsPanel.opened ||
                state.ui.audiobook.playlist.opened ||
                state.ui.plugin.opened
                    ? leftPanelOffset
                    : SIDEBAR_NO_OFFSET;

            const right = state.ui.settings.opened || state.ui.search.opened ? SIDEBAR_PANEL_OFFSET : SIDEBAR_NO_OFFSET;

            return { left, right };
        },
        isAndroid() {
            return detectAndroid();
        },
        isApple() {
            return detectApple();
        },
        // Farfalla's iframe, as opposed to the Fenice mobile and desktop shells.
        isWebHost(state, getters) {
            return !state.embeddedInMobileApp && !getters.isFeniceDesktopApp;
        },
        isPromoteAppEnabled(state, getters) {
            return getters.isWebHost && state.promoteApp.enabled;
        },
        showRangePaginator(state, getters) {
            if (getters.isPdf || (getters.isEpub && state.epub.spine?.length > 0)) {
                return true;
            }

            return false;
        },
        isZoomActive(state) {
            return state.ui.settings.zoom.select.scale !== 1;
        },
        allHighlighters(state) {
            // Combine built-in colors with custom colors
            // Both use the same structure: { slug, color, label }
            const builtInColors = DEFAULT_HIGHLIGHT_COLORS.map(color => ({
                slug: color.slug,
                color: color.color,
                label: color.label,
            }));
            const customColors = state.customColors.map(color => ({
                slug: color.slug,
                color: color.color,
                label: color.label,
                custom: true,
            }));
            return [...builtInColors, ...customColors];
        },
        /**
         * Whether the reader is still under the highlighter limit, which is what gates
         * every "new highlighter" control.
         */
        canAddMoreHighlighters(state, getters) {
            return getters.allHighlighters.length < MAX_TOTAL_TAGS;
        },

        /**
         * Resolves the active quick highlight color from the user's own palette.
         * Falls back to the first built-in color when unset or no longer available.
         * @returns {{ color: string, colorSlug: string }}
         */
        quickHighlightColor(state, getters) {
            const activeColor = getters.allHighlighters.find(
                highlighter => highlighter.slug === state.quickHighlight.activeColorSlug
            );
            const resolvedColor = activeColor || getters.allHighlighters[0];
            return { color: resolvedColor.color, colorSlug: resolvedColor.slug };
        },

        /**
         * The flashcard currently shown in the study session, or null when inactive/empty.
         */
        currentStudyCard(state) {
            if (!state.flashcardStudyMode.active || state.flashcardStudyMode.cards.length === 0) return null;
            return state.flashcardStudyMode.cards[state.flashcardStudyMode.currentIndex] || null;
        },

        /**
         * One-based position and total count for the study progress indicator.
         */
        studyProgress(state) {
            const { currentIndex, cards } = state.flashcardStudyMode;
            return { current: currentIndex + 1, total: cards.length };
        },

        /**
         * Aggregated per-session self-assessment counts for the study summary screen.
         */
        studyResults(state) {
            let know = 0;
            let dontKnow = 0;
            for (const rating of Object.values(state.flashcardStudyMode.ratings)) {
                if (rating === 'know') know++;
                else if (rating === 'dontKnow') dontKnow++;
            }
            return { know, dontKnow, total: state.flashcardStudyMode.cards.length };
        },

        usedHighlightColorSlugs(state) {
            const slugSet = new Set();
            for (const selection of state.selections) {
                if (selection.colorSlug) slugSet.add(selection.colorSlug);
            }
            return Array.from(slugSet);
        },
        usedColorSlugs(state) {
            const slugSet = new Set();
            for (const selection of state.selections) {
                if (selection.colorSlug) slugSet.add(selection.colorSlug);
            }
            for (const flashcard of state.flashcards) {
                if (flashcard.colorSlug) slugSet.add(flashcard.colorSlug);
            }
            return Array.from(slugSet);
        },
        isOpenSomeFeatureOrSettings(state) {
            const features = [
                state.ui.search,
                state.ui.thumbnails,
                state.ui.settings,
                state.ui.notebook,
                state.ui.addOnsPanel,
                state.ui.flashcardsPanel,
                state.ui.plugin,
                state.ui.rightSubmenu,
                state.ui.tableOfContents,
                state.ui.audiobook.playlist,
            ];

            return features.some(feature => feature?.opened === true);
        },
        /**
         * The panels starting to read aloud leaves open. Beyond a phone the player floats
         * over the right edge, so the left panels stay beside it. On a phone its sheet
         * covers the reader, so only a panel the reader is writing in stays, which closing
         * would drop unsaved. The notebook keeps its tabs: the open one holds the editor.
         */
        panelsBesideThePlayer(state, getters) {
            const notebook = ['notebook', 'noteEditor', 'highlights', 'notes', 'bookmarks'];
            if (!getters.onlyMobileVisible) {
                return [...notebook, 'tableOfContents', 'flashcardsPanel', 'addOnsPanel', 'plugin'];
            }

            return [
                ...(getters.addOnBeingEdited ? ['addOnsPanel'] : []),
                ...(state.ui.noteEditor.opened ? notebook : []),
            ];
        },
    },
    actions: {
        /**
         * Requests a controlled print job, shows the feedback toasts and reflects
         * the result in the lifetime counter and history.
         *
         * The unique-page discount is authoritative in the backend; here it is
         * applied optimistically so the UI updates while the backend is mocked.
         * Reprints from history (source: 'history') never consume quota nor add history.
         *
         * On success, emits a fire-and-forget `print-create` interaction event to
         * Coniglio (via the host) for every request — new prints and reprints alike —
         * carrying the backend job id and the range's page count, never page content.
         *
         * On failure, captures the error in Sentry and replaces the "preparing" toast
         * with a localized error message; the optimistic quota/history updates are
         * skipped so a failed job never consumes the user's allowance. No tracking
         * event is emitted for a failed job.
         *
         * A host that advertises reader delivery gets the PDF handed over in the
         * reader instead, through requestPrintPagesInReader.
         *
         * @param {Object} ctx - Vuex action context
         * @param {{ fileType: 'pdf'|'epub', pageFrom: number, pageTo: number, source: 'new'|'history' }} payload
         * @returns {Promise<import('../communication/print/PrintAPI').PrintPagesResult|null>}
         */
        async requestPrintPages({ state, commit }, payload) {
            if (state.printControl.readerDelivery) {
                return requestPrintPagesInReader({ state, commit }, payload);
            }

            const printStrings = state.localizations[state.lang].print;

            commit('openToast', { text: withPrintRange(printStrings.preparing, payload), durationMs: 60000 });
            try {
                const result = await printAPI.requestPrintPages(payload);
                commit('openToast', { text: withPrintRange(printStrings.ready, payload), durationMs: 5000 });

                interactionEventsTracker.trackPrint(result.jobId, printedPageCount(payload));

                countPrintedRange(commit, payload);
                return result;
            } catch (error) {
                console.error(error);
                Sentry.captureException(error);
                openPrintRequestFailedToast(commit, printStrings);
                return null;
            }
        },
        /**
         * Copies text to the clipboard via the host application.
         * Returns true on success, false if the feature is disabled or the copy fails.
         * On failure, captures the error in Sentry and shows a localized toast.
         * @param {Object} ctx - Vuex action context
         * @param {string} selectionText - Text to copy
         * @returns {Promise<boolean>}
         */
        async copyToClipBoard({ state, commit }, selectionText) {
            /**
             *  If the feature itself is disabled, which could be
             *  the intention of the content owner, then we return
             *  early and prevent the action.
             *  This is a fail-safe in case the action is
             *  triggered even if it shouldn't.
             */
            if (!state.copyContentToClipboard.enabled) {
                return false;
            }
            state.copyContentToClipboard.preventCopyToClipboardEvent = false;
            try {
                await uiAPI.copyToClipboard({ text: selectionText });
                return true;
            } catch (error) {
                Sentry.captureException(error);
                console.error('[copyToClipBoard] Failed to copy to clipboard:', error);
                const fallback =
                    state.localizations[state.lang]?.sharing?.copyToClipboardFailed ||
                    state.localizations.en?.sharing?.copyToClipboardFailed;
                commit('openToast', { text: fallback });
                return false;
            } finally {
                state.copyContentToClipboard.preventCopyToClipboardEvent = true;
            }
        },
        /**
         * Our default action is to prevent content to be copied to the clipboard.
         * The copying to the clipboard event may be generated by copy or cut actions.
         * Those may be triggered by a browser or OS native UI,
         * by keyboard shortcuts or even from JS itself.
         */
        preventUnauthorizedCopy({ state, _getters }, e) {
            /**
             *  If copy and cut events are temporality allowed,
             *  return early and allow the content to be copied.
             *  This flag is controlled exclusively by the
             *  copyToClipBoard method, and it's used to
             *  temporality allow the action.
             */
            if (!state.copyContentToClipboard.preventCopyToClipboardEvent) {
                return;
            }
            e.preventDefault();
            e.clipboardData.setData('text/plain', '');
        },
        trackOnboarding({ _state, _getters }, _payload) {
            /**
             * We used to use Mixpanel.
             * I'm leaving this here cause we'll implement something similar at some point.
             */
        },
        trackEvent({ _getters }, _payload) {
            /**
             * We used to use Mixpanel.
             * I'm leaving this here cause we'll implement something similar at some point.
             */
        },
        async goToNextPage({ dispatch, state, getters, commit }, payload) {
            if (state.isFixedLayout && state.isLoading) return;
            // Ahead of the animation and of the host: an arrow the operator
            // then refuses must leave neither a slide nor an end-of-book behind.
            if (addOnEditorHoldsPageTurn({ state, getters, commit }, { action: 'goToNextPage', payload })) return;
            if (state.disableNext) {
                /*
                 * Show payments link when an user go to last page in the preview and
                 * the issue need is purchable.
                 */
                navigationAPI.reachLastPage();
                return;
            }

            const direction = 'next';
            await dispatchAnimation(direction, payload?.skipAnimation ?? getters.scrollPageMode);
            if (getters.isEpub) {
                dispatch('goToLocation', { direction, [ADD_ON_EDITOR_CONFIRMED]: true });
                return;
            }

            const offset = getters.doublePageMode && getters.currentPage !== 1 ? 2 : 1;
            const destPage =
                getters.currentPage < getters.publicationNumberOfPages
                    ? getters.currentPage + offset
                    : getters.publicationNumberOfPages;

            // In PDFs if destPage is the last page, it means that the user is at the end of the publication
            // The host is responsible for showing the payments link or any other action
            if (destPage === getters.publicationNumberOfPages) {
                navigationAPI.reachLastPage();
            }
            dispatch('goToPage', {
                page: destPage,
                action: 'next',
                [ADD_ON_EDITOR_CONFIRMED]: true,
            });
        },
        async goToPrevPage({ dispatch, state, getters, commit }, payload) {
            if (state.isFixedLayout && state.isLoading) return;
            if (addOnEditorHoldsPageTurn({ state, getters, commit }, { action: 'goToPrevPage', payload })) return;

            commit('setLastPageReached', false);
            state.disableNext = false;

            const direction = 'prev';
            await dispatchAnimation(direction, payload?.skipAnimation ?? getters.scrollPageMode);
            if (getters.isEpub) {
                dispatch('goToLocation', { direction, [ADD_ON_EDITOR_CONFIRMED]: true });
                return;
            }
            const offset = getters.doublePageMode && getters.currentPage !== 1 && getters.currentPage !== 2 ? 2 : 1;
            const destPage = getters.currentPage > 1 ? getters.currentPage - offset : 1;
            dispatch('goToPage', {
                page: destPage,
                action: 'prev',
                [ADD_ON_EDITOR_CONFIRMED]: true,
            });
        },
        /**
         * The page a scroll brought under the reader, recorded the way a turn
         * is. It is named apart from `goToPage` because nobody asked for it:
         * the reader followed the content rather than turning to it, so there
         * is no gesture for an open add-on form to ask about.
         */
        followScrolledPage({ dispatch }, payload) {
            return dispatch('goToPage', { ...payload, [ADD_ON_EDITOR_CONFIRMED]: true });
        },
        goToPage({ state, getters, commit, dispatch }, payload) {
            if (addOnEditorHoldsPageTurn({ state, getters, commit }, { action: 'goToPage', payload })) return;

            commit('closeSelectionTools');

            let destPage;
            // console.log('payload.page', payload.page);
            if (!getters.doublePageMode) {
                // console.log('single page mode');
                destPage = payload.page;
            } else if (payload.page === 1) {
                // console.log('payload.page === 1');
                // In doublePageMode the first page is displayed individually
                destPage = payload.page;
            } else if (payload.page === getters.publicationNumberOfPages) {
                // console.log('payload.page === getters.publicationNumberOfPages');
                // In doublePageMode the first page is displayed individually
                destPage = payload.page;
            } else if (payload.page % 2 !== 0) {
                // console.log('payload.page % 2', payload.page % 2);
                // In doublePageMode the last page might be displayed individually
                destPage = payload.page - 1;
            } else if (payload.page % 2 === 0 && payload.page - 1 === getters.publicationNumberOfPages) {
                // console.log('payload.page % 2', payload.page % 2, getters.currentPages[0]);

                if (getters.currentPages[0] === payload.page - 2) {
                    // console.log('payload.page - 2', payload.page - 2)
                    destPage = payload.page - 2;
                } else {
                    // console.log('payload.page - 1', payload.page - 1)
                    destPage = payload.page - 1;
                }
            } else {
                // console.log('eeelse', payload.page);
                destPage = payload.page;
            }

            // console.log('destPage', destPage);

            // @here
            // Do nothing if destPage === currentPage

            commit('setPage', { page: destPage });
            dispatch('notifyLocationChange');
        },
        goToLocation({ state, getters, commit, _dispatch }, payload) {
            if (addOnEditorHoldsPageTurn({ state, getters, commit }, { action: 'goToLocation', payload })) return;

            commit('closeSelectionTools');

            // TODO: state.epubViewer should eventually replace state.readium here.
            // Currently both coexist: state.readium stores the EpubNavigator instance
            // and state.epubViewer stores the EpubViewer wrapper. Consolidate into
            // a single reference to avoid dual-state confusion.
            if (getters.isEpub && state.readium) {
                // Readium 2 navigation
                if (payload.direction === 'next') {
                    // eslint-disable-next-line no-restricted-syntax -- this is the turn itself: goToNextPage applied the gates before dispatching it
                    state.readium.goRight(true, success => {
                        // A falsy `success` only means the navigator did not move. Since
                        // navigator 2.6.0 that also happens when another navigation is still
                        // in flight, so it cannot stand in for "end of the book" on its own —
                        // treating it as one disabled paging for the rest of the session and
                        // told the host the reader had finished, mid-book.
                        const atEnd =
                            state.epubViewer?.isLastPageVisible() || (!success && state.readium.canGoForward === false);

                        if (atEnd) {
                            state.disableNext = true;
                            navigationAPI.reachLastPage();
                        }
                    });
                } else if (payload.direction === 'prev') {
                    // eslint-disable-next-line no-restricted-syntax -- this is the turn itself: goToPrevPage applied the gates before dispatching it
                    state.readium.goLeft(true, () => {});
                } else if (payload.epubR2Location?.locations) {
                    // R2 navigation from epubR2Location (notes/highlights).
                    // Routed through the wrapper so the jump runs inside a
                    // tracking-suppression window (one page-change with the
                    // final destination); raw navigator as fallback.
                    const locator = createR2Locator(payload.epubR2Location);
                    if (state.epubViewer?.goToLocator) {
                        state.epubViewer.goToLocator(locator);
                    } else {
                        state.readium.go(locator, true, () => {});
                    }
                } else if (payload.epubPrintedPageLocation?.locations) {
                    state.epubViewer?.goToPrintedPage(createR2Locator(payload.epubPrintedPageLocation));
                } else if (payload.epubR2PositionIndex !== undefined) {
                    // R2 navigation by position index (from range paginator)
                    if (state.epubViewer?.navigateToPositionIndex) {
                        state.epubViewer.navigateToPositionIndex(payload.epubR2PositionIndex);
                    } else {
                        const locator = state.readium.positions[payload.epubR2PositionIndex];
                        if (locator) {
                            state.readium.go(locator, true, () => {});
                        }
                    }
                } else if (payload.epubLocation) {
                    // Legacy R1 format: delegate to EpubViewer for idref resolution and navigation
                    state.epubViewer?.goToR1Location(payload.epubLocation, payload.totalText);
                } else if (payload.chapterHref) {
                    // From Table of Contents
                    if (state.epubViewer?.goToLink) {
                        state.epubViewer.goToLink({ href: payload.chapterHref });
                    } else {
                        const link = new Link({ href: payload.chapterHref });
                        state.readium.goLink(link, true, () => {});
                    }
                }
            } else if (state.readium?.reader) {
                // Legacy R1 fallback (should not happen after full migration)
                if (payload.direction === 'next') {
                    state.readium.reader.openPageRight();
                } else if (payload.direction === 'prev') {
                    state.readium.reader.openPageLeft();
                }
            }
        },

        setLastLocation({ state, getters, _commit, dispatch }, payload) {
            if (getters.isEpub) {
                // Support both R2 locator format and legacy R1 format
                if (payload.locator) {
                    // R2 format: store the full locator
                    state.lastLocation.epub = {
                        locator: payload.locator,
                        // Keep legacy fields for backward compatibility with host apps
                        chapterHref: payload.locator.href || '',
                        cfi: '',
                    };
                } else {
                    // Legacy R1 format
                    state.lastLocation.epub.chapterHref = payload.chapterHref;
                    state.lastLocation.epub.cfi = payload.cfi;
                }
            }
            // Deliberately format-specific, not `isPlayableMedia`: this writes the audio
            // payload (seek + track), and video will get its own `lastLocation.video`.
            if (getters.isAudiobook) {
                state.lastLocation.audio.currentSeek = payload.currentSeek;
                state.lastLocation.audio.currentTrack = payload.currentTrack;
            }
            if (payload.dontNotify) return;
            dispatch('notifyLocationChange');
        },
        notifyLocationChange({ state, getters }) {
            let location = {};

            if (getters.isPdf) {
                location = { page: getters.currentPage };
            }
            if (getters.isEpub) {
                // Send R2 locator data to host if available, otherwise legacy format
                const epubLocation = state.lastLocation.epub;
                if (epubLocation.locator) {
                    location = {
                        locator: epubLocation.locator,
                        // Include legacy fields for backward compatibility
                        chapterHref: epubLocation.chapterHref,
                        cfi: epubLocation.cfi,
                    };
                } else {
                    location = epubLocation;
                }
            }

            // Deliberately format-specific, not `isPlayableMedia`: the host is sent the audio
            // payload, and video will report its own `lastLocation.video`.
            if (getters.isAudiobook) {
                location = state.lastLocation.audio;
            }

            localStorage?.setItem('notifyLocationChange', JSON.stringify(location));

            /**
             * It is up to the host to throttle these events.
             */
            navigationAPI.locationChanged({
                location,
                fileType: state.fileType,
            });
        },
        setDisableNext({ state }, payload) {
            state.disableNext = payload.disableNext;
        },

        async fetchSelections({ state, commit }) {
            if (state.anonymousSession) {
                commit('fetchedSelections');
                return;
            }

            if (state.selectionsHaveBeenFetched) return;
            try {
                // setHighlightModel can land while this request is in flight, so the model
                // is read after the response rather than alongside it.
                const response = await notesAPI.list();
                const highlights = await hydrateHighlightsFromHost(state.highlightModel, response);
                commit('setHighlights', { highlights });
                commit('fetchedSelections');
            } catch (e) {
                console.error('fetchSelections failed', e);
                Sentry.captureException(e, { tags: { feature: 'highlights' } });
                commit('setHighlights', {
                    highlights: [],
                });
                commit('fetchedSelections');
            }
        },
        async fetchBookmarks({ getters, commit }) {
            // No `fetchedBookmarks` on the early return, unlike fetchSelections: a session
            // canUseBookmarks rejects (feature off, anonymous, or an audiobook) has no list
            // and no entry points, so leaving the flag false keeps toggleBookmark inert for
            // the whole session.
            if (!getters.canUseBookmarks) return;
            try {
                const bookmarks = await bookmarksAPI.list();
                commit('setBookmarks', { bookmarks });
                commit('fetchedBookmarks');
            } catch (e) {
                console.error('fetchBookmarks failed', e);
                Sentry.captureException(e, { tags: { feature: 'bookmarks' } });
                commit('setBookmarks', { bookmarks: [] });
                // A failed fetch still settles the list: blocking the toggle for the rest of
                // the session would be worse than letting a create through on an empty one.
                commit('fetchedBookmarks');
            }
        },
        // #tts
        listeningPage({ state, getters, commit }) {
            const isListeningPage = state.ui.listening.texToListen === state.ui.listening.pageToListen;
            commit('setUuidToListen');
            if (state.ui.listening.opened && isListeningPage) {
                commit('closeListeningPlayer');
            } else if (isListeningPage) {
                commit('closeAllSettingsOrFeatures', { featureKey: getters.panelsBesideThePlayer });
                commit('openListeningPlayer');
            } else {
                commit('closeAllSettingsOrFeatures', { featureKey: getters.panelsBesideThePlayer });
                commit('openListeningPlayer');
                commit('loadPageTextToTTS');
            }
        },
        listeningHighlights({ getters, commit }, payload) {
            commit('setTextToListen', { text: payload.text });
            commit('setUuidToListen', { uuidToListen: payload.uuidToListen });
            commit('closeAllSettingsOrFeatures', { featureKey: getters.panelsBesideThePlayer });
            commit('openListeningPlayer');
        },

        setTheme({ commit, dispatch, state }, payload) {
            if (BASE_THEMES.includes(payload.theme)) {
                commit('setBaseTheme', { theme: payload.theme });
            }

            let colorScheme = payload.theme;
            commit('setUiTheme', { theme: payload.theme });
            if (state.ui.theme === 'system') {
                colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }

            commit('setThemeSelected', { theme: colorScheme });
            dispatch('applyTheme', { theme: payload.theme });
        },
        applyTheme({ _state }, payload) {
            const body = document.querySelector('body');
            if (isDarkTheme(payload.theme)) {
                body.classList.add('dark');
            } else {
                body.classList.remove('dark');
            }

            localStorage.setItem('setTheme', payload.theme);
        },

        /**
         * Applies a settings change and saves the full settings to the host.
         * Rejects when the save fails; UI code that does not wait for it uses saveUserSettings.
         * @param {Object} payload - The changed settings
         */
        updateUserSettings({ state }, payload = {}) {
            const hasEpubChanges = payload.epubLayout || payload.epubZoom || payload.fontFamily !== undefined;

            if (hasEpubChanges) {
                state.userSettings.epub = {
                    ...state.userSettings.epub,
                    ...(payload.epubLayout && { layout: payload.epubLayout }),
                    ...(payload.epubZoom && { zoom: payload.epubZoom }),
                    ...(payload.fontFamily !== undefined && { fontFamily: payload.fontFamily }),
                };
            }

            if (payload.pdfLayout) {
                state.userSettings.pdf = { ...state.userSettings.pdf, layout: payload.pdfLayout };
            }

            if (payload.theme) {
                state.userSettings.theme = payload.theme;
            }

            if (state.anonymousSession) return;
            state.userSettings.epub.layout =
                payload.epubLayout !== undefined ? payload.epubLayout : state.userSettings.epub.layout;
            state.userSettings.epub.zoom =
                payload.epubZoom !== undefined ? payload.epubZoom : state.userSettings.epub.zoom;
            state.userSettings.epub.fontFamily =
                payload.fontFamily !== undefined ? payload.fontFamily : state.userSettings.epub.fontFamily;
            state.userSettings.epub.lineHeight =
                payload.lineHeight !== undefined ? payload.lineHeight : state.userSettings.epub.lineHeight;
            state.userSettings.epub.margins =
                payload.margins !== undefined ? payload.margins : state.userSettings.epub.margins;
            state.userSettings.pdf.layout =
                payload.pdfLayout !== undefined ? payload.pdfLayout : state.userSettings.pdf.layout;
            state.userSettings.theme = payload.theme !== undefined ? payload.theme : state.userSettings.theme;

            state.userSettings.customColors = state.customColors;
            state.userSettings.quickHighlight = { ...state.quickHighlight };
            return settingsAPI.update({ settings: state.userSettings });
        },
        /**
         * Saves a settings change made from the reader UI, which does not wait
         * for the save, and tells the user when it fails. A failure that a
         * newer save already covers only reaches Sentry.
         * @param {Object} payload - Same as updateUserSettings
         */
        async saveUserSettings({ state, commit, dispatch }, payload) {
            const save = ++latestSettingsSave;
            try {
                await dispatch('updateUserSettings', payload);
            } catch (error) {
                if (save !== latestSettingsSave) {
                    Sentry.captureException(error);
                    return;
                }
                reportSettingsSaveFailure({ state, commit }, error, { offerRetry: true });
            }
        },
        async resetUserSettings({ state, commit, dispatch }) {
            dispatch('setTheme', { theme: 'light' });
            commit('fontSizeUpdate', { fontSize: FONT_SIZE_DEFAULT });
            commit('setQuickHighlightEnabled', false);
            commit('setQuickHighlightColor', null);
            state.userSettings = DEFAULT_USER_SETTINGS();
            if (!state.anonymousSession) {
                try {
                    await settingsAPI.update({ settings: state.userSettings });
                } catch (error) {
                    reportSettingsSaveFailure({ state, commit }, error, { offerRetry: true });
                }
                return;
            }
            commit('setReaderLayout', 'single');
        },
        setUserSettings({ state, commit, getters }, settings) {
            state.userSettings = settings;

            if (BASE_THEMES.includes(state.userSettings.theme)) {
                commit('setBaseTheme', { theme: state.userSettings.theme });
            }
            commit('setUiTheme', { theme: state.userSettings.theme });
            if (getters.isEpub) {
                // Prevent scroll mode in EPUB previews
                const epubLayout = ((state, getters) => {
                    let preliminaryEpubLayout = state.userSettings.epub.layout || state.reader.layout || 'single';
                    if (preliminaryEpubLayout === 'scroll' && getters.isPreviewMode) {
                        preliminaryEpubLayout = 'single';
                    }
                    return state.epubLayouts[preliminaryEpubLayout];
                })(state, getters);

                const zoomFromApi = state.userSettings.epub.zoom;
                const storedFontSize = state.settings?.fontSize;
                let zoomInRem = storedFontSize;
                if (zoomFromApi) {
                    if (zoomFromApi >= API_FONT_SIZE_MIN && zoomFromApi <= API_FONT_SIZE_MAX) {
                        zoomInRem = zoomFromApi / API_FONT_SIZE_BASE;
                    } else if (zoomFromApi >= FONT_SIZE_MIN && zoomFromApi <= FONT_SIZE_MAX) {
                        zoomInRem = zoomFromApi;
                    }
                }

                commit('fontSizeUpdate', { fontSize: zoomInRem });
                commit('setEpubLayoutMode', epubLayout);
            }
            if (state.userSettings.pdf.layout && getters.isPdf) {
                commit('setReaderLayout', state.userSettings.pdf.layout);
            }
            if (getters.isPdf && getters.doublePageMode && getters.onlyMobileVisible) {
                commit('setReaderLayout', 'single');
            }
        },

        /**
         * Opens the notebook, if it is closed, and shows its notes tab.
         *
         * The reader controls come back first. Panels are laid out under the header band
         * (`--ReaderHeaderHeight`), which a hidden header still reserves, so a notebook
         * opened from a reader gesture — tapping a highlight that carries a note, writing
         * one from the selection tool — while the controls are away leaves an empty strip
         * top and bottom where the toolbars belong. Revealing afterwards is not an option:
         * `toggleUi` closes every open panel, the notebook included, unless the add-ons form
         * is open.
         */
        openNotebookOnNotesTab({ dispatch, state }) {
            if (state.uiHidden) dispatch('toggleUi', { uiHidden: false });
            if (!state.ui.notebook.opened) dispatch('toggleNotebook');
            dispatch('toggleNotes');
        },

        /**
         * Writing a note happens in the notebook: the panel opens on its notes tab
         * with the editor at the top of the list, or in place of the note being edited.
         * A note written by another reader is read-only for the subscriber, so it is
         * only brought into view instead.
         * @param {{uuid?: string}} payload - The note to edit; omitted to write a new one
         */
        openNoteEditor({ commit, dispatch, state }, payload = {}) {
            const note = payload.uuid && state.selections.find(selection => selection.uuid === payload.uuid);
            if (isAnnotationFromAnotherReader(note)) {
                dispatch('focusNoteInNotebook', { uuid: note.uuid });
                return;
            }
            dispatch('openNotebookOnNotesTab');
            commit('openNoteEditor', payload);
        },

        /**
         * Brings a stored note into view in the notebook without opening its editor: the
         * panel opens on its notes tab and scrolls to the note's row. An editor left open
         * on another note is closed, which only opening the notebook would have done.
         * @param {{uuid: string}} payload - The note to show
         */
        focusNoteInNotebook({ commit, dispatch }, { uuid }) {
            commit('closeNoteEditor');
            dispatch('openNotebookOnNotesTab');
            commit('setNoteFocusUuid', uuid);
        },

        /**
         * Persists a new color and annotation on a highlight the host already has, from
         * wherever the reader edits it. Text layers repaint from `selections` on their
         * own, so the highlight's page does not have to be on screen. A save that changes
         * nothing never reaches the host, and a rejected one leaves `selections` untouched.
         *
         * The edit is written on a copy, never on the highlight itself: `update()` swaps a
         * new object into `selections`, and the text layers decide whether to repaint by
         * comparing that list against the previous one — an in-place write would leave the
         * previous entry already carrying the new color, and nothing would repaint.
         * @param {Object} payload
         * @param {Object} payload.highlight - The stored highlight, as held in `selections`
         * @param {string} payload.color
         * @param {string} payload.colorSlug
         * @param {string} payload.annotationText
         */
        async updateStoredHighlight(
            { commit, dispatch, state },
            { highlight, color, colorSlug, annotationText, annotationFormat = null }
        ) {
            // The rich editor opens a note with its markup (a legacy plain note
            // converted to paragraphs first), so "unchanged" compares against what
            // the editor opened with, not against the plain-text projection the
            // note also carries.
            const openedWith =
                annotationFormat === 'html'
                    ? highlight.annotationHtml || plainTextToEditorHtml(highlight.annotationText || '')
                    : highlight.annotationText;
            const isUnchanged =
                Boolean(highlight.hasAnnotation) &&
                highlight.color === color &&
                highlight.colorSlug === colorSlug &&
                openedWith === annotationText;
            if (isUnchanged) {
                commit('setCurrentHighlight', highlight);
                return;
            }

            // A plain-text edit is the whole annotation from now on: keeping the
            // stored markup would make the save re-send it over the edit.
            const edited = {
                ...highlight,
                color,
                colorSlug,
                annotationText,
                annotationFormat,
                annotationHtml: annotationFormat === 'html' ? highlight.annotationHtml : null,
                hasAnnotation: true,
            };
            await edited.update();
            edited.updateAnnotationText();

            const stored = state.selections.find(selection => selection.uuid === highlight.uuid) ?? edited;
            commit('setCurrentHighlight', stored);
            if (highlight.colorSlug !== colorSlug) dispatch('rememberQuickHighlightColor', colorSlug);
        },

        showSessionExpiredModal({ _state, commit }) {
            commit('setSessionExpired');
        },

        buildUrlAndGoToSocialNetworksSharing(
            { state, _dispatch },
            payload = {
                url: null,
                network: null,
                title: null,
                authors: null,
                highlight: null,
                readerUrl: null,
            }
        ) {
            let url = payload.url;

            if (payload.network === 'x' && state.issue.twitterUser === '') {
                url = url.replace('&via=@xuser', '');
            }

            if (!payload.authors) {
                url = url.replace('@by%20@authors%20', '');
            }

            uiAPI.shareContent({
                url: url
                    .replace(/@iamread/g, encodeURIComponent(state.localizations[state.lang].imReading))
                    .replace(/@highlight/g, encodeURIComponent(payload.highlight))
                    .replace(/@title/g, encodeURIComponent(payload.title))
                    .replace(/@by/g, encodeURIComponent(state.localizations[state.lang].by.toLowerCase()))
                    .replace(/@authors/g, encodeURIComponent(payload.authors))
                    .replace(/@in/g, encodeURIComponent(state.localizations[state.lang].in))
                    .replace(/@url/g, encodeURIComponent(payload.readerUrl))
                    .replace(
                        /@description/g,
                        encodeURIComponent(state.issue.descriptionRawText ? state.issue.descriptionRawText : '')
                    )
                    .replace(/@twitteruser/g, state.issue.twitterUser),
                target: payload.network === 'email' || state.embeddedInMobileApp ? '_top' : '_blank',
            });
        },
        offerOnboardingTour({ commit }) {
            try {
                if (localStorage.getItem('onboardingTourOffered:v1') === 'true') return;
                localStorage.setItem('onboardingTourOffered:v1', 'true');
            } catch (_error) {
                settingsAPI.cookiesAreDisabled();
            }
            commit('openTourBanner');
        },
        initToggleUi({ getters, commit }, { hiddenOnOpen = false } = {}) {
            // Only text formats reveal the controls on tap; playable media and the
            // unsupported screen open with them up whatever the host asks.
            const uiHidden = hiddenOnOpen && getters.hasTextContent;
            commit('setStateUiHidden', uiHidden);
            void uiAPI.uiVisibilityChanged({ isVisible: !uiHidden });
        },
        /**
         * Back, from the reader's button or the app's: steps back from what
         * opened last, then closes what the header opened, then stops reading
         * aloud, and leaves the reader once nothing is open. An embedded reader
         * has nowhere to go back to.
         */
        goBack({ state, getters, commit }) {
            if (stepBack()) return;

            if (getters.isOpenSomeFeatureOrSettings) {
                commit('closeAllSettingsOrFeatures');
            } else if (state.ui.listening.opened) {
                commit('closeListeningPlayer');
            } else if (!getters.isEmbeddedMode) {
                uiAPI.close();
            }
        },
        /** The app's back, which only the app's reader answers. */
        goBackInApp({ state, dispatch }) {
            if (state.embeddedInMobileApp) dispatch('goBack');
        },
        openTranslateIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'translate',
                text: payload.text,
            });
        },
        openDictionaryIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'dictionary',
                text: payload.text,
            });
        },
        openAiIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'ai-integrations',
                text: payload.text,
                action: payload.action,
            });
        },
        openIntegrationTool({ commit }, payload) {
            commit('toggleIntegrationTool', {
                opened: true,
                name: payload.name,
                text: payload.text,
                action: payload.action,
            });
        },
        closeIntegrationTool({ commit }) {
            commit('toggleIntegrationTool', {
                opened: false,
                integration: null,
                text: '',
            });
        },
        setCurrentEpubProgress({ state, commit }, payload) {
            if (payload.spineItem || payload.openPage) {
                // We need to add +1 in separated way to avoid NaN when the first value is index == 0
                commit('setPage', {
                    page: payload.spineItem?.index + 1 || payload.openPage.spineItemIndex + 1,
                });
                commit('setSubSpine', {
                    subSpine: {
                        pages: payload.openPage?.spineItemPageCount || payload.spineItem?.paginationInfo?.spreadCount,
                        position:
                            payload.openPage?.spineItemPageIndex + 1 ||
                            payload.spineItem?.paginationInfo?.currentSpreadIndex + 1,
                    },
                });
                return;
            }

            const cfi = state.readium.reader.getFirstVisibleCfi();
            const spineLocation = state.epub.spine?.findIndex(spine => spine.href.includes(cfi.idref));
            commit('setPage', { page: spineLocation + 1 || 1 });
        },
        async fetchAiActions({ state, commit }) {
            if (!state.AiIntegrations.enabled) return;

            const actions = await aiIntegrationsAPI.listActions().catch(_error => {
                return [];
            });

            commit('aiActions', actions || []);
        },

        /// new ones
        toggleUi({ state, getters, commit }, payload) {
            const isHidden = payload?.uiHidden === undefined ? !state.uiHidden : Boolean(payload.uiHidden);
            // The form holds unsaved work and a panel the chrome closes drops its form. A reveal
            // still goes through because the skip link and the openers of other features (search,
            // notebook, flashcards) ask for one.
            const addOnFormIsOpen = Boolean(getters.addOnBeingEdited);
            if (addOnFormIsOpen && isHidden) return;

            if (isHidden !== state.uiHidden) {
                void uiAPI.uiVisibilityChanged({ isVisible: !isHidden });
            }
            if (!addOnFormIsOpen) commit('closeAllSettingsOrFeatures');
            commit('setStateUiHidden', isHidden);
        },

        toggleSettings({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'settings' });
            commit('toggleFeature', { featureKey: 'settings' });
        },

        /**
         * Ask the host to flip fullscreen and adopt the state it reports back. The host owns
         * the real state, so a denied request leaves the icon showing what is actually on
         * screen instead of an optimistic guess.
         */
        async toggleFullscreen({ state, commit, dispatch }) {
            if (fullscreenRequestInFlight) return;
            fullscreenRequestInFlight = true;

            dispatch('trackEvent', {
                action: 'Fullscreen - toggle',
                trigger: 'click',
                page: '',
                source: 'toolbar',
            });

            try {
                // A null answer means the host predates the fullscreen contract; the toggle
                // is a quiet no-op until the host catches up.
                const result = await uiAPI.setFullscreen({ isFullscreen: !state.isFullscreen });
                if (result) {
                    commit('setFullscreenState', result.isFullscreen);
                }
            } catch (error) {
                // A timeout, a disconnected bridge, or a handler that blew up is a real
                // failure and has to be reported.
                console.error('[toggleFullscreen] the fullscreen request failed:', error);
                Sentry.captureException(error);
            } finally {
                fullscreenRequestInFlight = false;
            }
        },

        /**
         * Mirror a fullscreen change the reader did not request (Esc, or an exit the OS
         * forced), pushed by the host through the `fullscreenChanged` command.
         *
         * @param {boolean} isFullscreen - Current fullscreen state on the host
         */
        updateFullscreenState({ commit }, isFullscreen) {
            commit('setFullscreenState', isFullscreen);
        },

        toggleRightSubmenu({ commit, _state }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'rightSubmenu' });
            commit('toggleFeature', { featureKey: 'rightSubmenu' });
        },

        toggleTableOfContents({ commit, _state }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'tableOfContents' });
            commit('toggleFeature', { featureKey: 'tableOfContents' });
        },

        toggleSearch({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'search' });
            commit('toggleFeature', { featureKey: 'search' });
        },

        openSearch({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'search' });
            commit('openFeature', { featureKey: 'search' });
        },

        toggleNotebook({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'notebook' });
            commit('toggleFeature', { featureKey: 'notebook' });
        },

        showHighlightsAndNotesTab({ commit, state }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'highlights' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'notes' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'bookmarks' });
            if (state.ui.selectionTool.opened) {
                commit('hideSelectionTool');
            }
        },

        toggleHighlights({ commit }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'notes' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'bookmarks' });
            commit('openFeature', { featureKey: 'features', subFeature: 'highlights' });
        },

        toggleNotes({ commit }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'highlights' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'bookmarks' });
            commit('openFeature', { featureKey: 'features', subFeature: 'notes' });
        },

        showBookmarksTab({ commit }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'highlights' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'notes' });
            commit('openFeature', { featureKey: 'features', subFeature: 'bookmarks' });
        },

        /**
         * Recomputes which bookmark (if any) is anchored within the currently visible page
         * and commits its uuid reactively. Triggered by the header on page/position change
         * and when the bookmark list changes. Detection is format-specific: a paginated EPUB
         * "page" spans a *range* of positions/progression, so the EPUB viewer owns the range
         * check against its navigator viewport; PDF pages are discrete.
         */
        refreshCurrentPageBookmark({ state, getters, commit }) {
            let match = null;
            if (getters.isEpub) {
                match = state.epubViewer?.getCurrentPageBookmark(getters.bookmarks) ?? null;
            } else if (getters.hasTextContent) {
                // TODO: move this PDF page match into a PDF viewer method (e.g.
                // state.pdfViewer.getCurrentPageBookmark), mirroring the EPUB branch above,
                // once the readers expose a shared polymorphic viewer instance. PDF pages are
                // discrete, so page-number equality is correct here.
                match = getters.bookmarks.find(bookmark => bookmark.page === getters.currentPage) ?? null;
            }
            commit('setCurrentPageBookmark', { uuid: match?.uuid ?? null });
        },

        async toggleBookmark({ state, getters, commit, dispatch }) {
            // The list is the only thing that says whether this page is already bookmarked, so
            // acting on an unsettled one reads every page as unbookmarked: a tap while the
            // initial fetch is still in flight creates a second bookmark over an existing one,
            // and the arriving list then drops the local copy. The window is one Delfino round
            // trip as the reader paints, so dropping the tap silently is the honest response —
            // there is nothing to report and nothing the reader could do about it.
            if (!state.bookmarksHaveBeenFetched) return;
            if (bookmarkToggleInFlight) return;
            bookmarkToggleInFlight = true;

            const t = state.localizations[state.lang] ?? {};

            try {
                const existing = getters.currentPageBookmark;

                if (existing) {
                    const removed = await existing.destroy();
                    if (!removed) {
                        commit('openToast', { text: t.bookmarkFailed });
                        return;
                    }
                    commit('removeBookmark', { uuid: existing.uuid });
                    commit('openToast', { text: t.bookmarkRemoved });
                    dispatch('refreshCurrentPageBookmark');
                    return;
                }

                const bookmark = buildBookmarkAtCurrentLocation(state, getters);
                if (!bookmark) {
                    commit('openToast', { text: t.bookmarkFailed });
                    return;
                }

                bookmark.create();
                await bookmark.store();
                commit('openToast', { text: t.bookmarkAdded });
                dispatch('refreshCurrentPageBookmark');
            } catch (e) {
                console.error('toggleBookmark failed', e);
                commit('openToast', { text: t.bookmarkFailed });
            } finally {
                bookmarkToggleInFlight = false;
            }
        },

        /**
         * Removes a bookmark from the Notebook list (X button). Named distinctly from the
         * `removeBookmark` mutation it commits, to avoid action/mutation name confusion. Only
         * removes it locally once the host delete confirms, otherwise shows an error toast and
         * leaves the list untouched (the bookmark would reappear on the next fetch anyway).
         */
        async deleteBookmark({ state, commit, dispatch }, { uuid }) {
            const bookmark = state.bookmarks.find(item => item.uuid === uuid);
            if (!bookmark) return;
            const t = state.localizations[state.lang] ?? {};
            const removed = await bookmark.destroy();
            if (!removed) {
                commit('openToast', { text: t.bookmarkFailed });
                return;
            }
            commit('removeBookmark', { uuid });
            dispatch('refreshCurrentPageBookmark');
        },

        toggleFlashcardsPanel({ state, commit, dispatch }) {
            const isClosing = state.ui.flashcardsPanel.opened;
            commit('closeAllSettingsOrFeatures', { featureKey: 'flashcardsPanel' });
            commit('toggleFeature', { featureKey: 'flashcardsPanel' });
            if (isClosing && state.flashcardStudyMode.active) dispatch('endStudyMode');
        },

        ...addOnsActions,
        ...addOnBatchActions,
        ...toursActions,

        /**
         * Opens the flashcards panel (without toggling it closed if already open) and flags the
         * given flashcard so the panel scrolls to it. Triggered by clicking a flashcard underline
         * in the reader, so the reader controls come back with it for the reason spelled out in
         * openNotebookOnNotesTab: the panel is laid out under a header band that stays reserved
         * while the header is hidden, and revealing afterwards would close the panel again
         * unless the add-ons form is open.
         * @param {Object} ctx - Vuex action context
         * @param {Object} flashcard - The clicked flashcard model instance
         */
        focusFlashcardInPanel({ commit, dispatch, state }, flashcard) {
            if (!flashcard?.uuid) return;
            if (state.uiHidden) dispatch('toggleUi', { uiHidden: false });
            commit('closeAllSettingsOrFeatures', { featureKey: 'flashcardsPanel' });
            commit('openFeature', { featureKey: 'flashcardsPanel' });
            commit('setFlashcardFocusUuid', flashcard.uuid);
        },

        /**
         * Opens the print-control modal, closing every other panel/feature except
         * the modal itself and the read-aloud player. Mirrors focusFlashcardInPanel: closeAllSettingsOrFeatures
         * with an exclusion key guarantees the just-opened modal survives, without
         * relying on the order of separate commits.
         */
        openPrintControl({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'printModal' });
            commit('openFeature', { featureKey: 'printModal' });
        },

        async fetchFlashcards({ state, getters, commit }) {
            // The host refuses this list by design where the feature is unavailable (feature
            // off, anonymous, or an audiobook), so a refusal is never an anomaly to report.
            if (!getters.canUseFlashcards) {
                commit('setFlashcardsFetched');
                return;
            }

            try {
                // setFlashcardModel can land while this request is in flight, so the model
                // is read after the response rather than alongside it.
                const response = await flashcardsAPI.list();
                const flashcards = await hydrateFlashcardsFromHost(state.flashcardModel, response);
                commit('setFlashcards', { flashcards });
            } catch (e) {
                console.error('fetchFlashcards failed', e);
                Sentry.captureException(e, { tags: { feature: 'flashcards' } });
                commit('setFlashcardsFetched');
            }
        },

        /**
         * Opens the study session with all flashcards, optionally shuffled. The current
         * fullscreen layout is preserved so reshuffling does not exit fullscreen. Starting
         * a session always resets the per-session ratings.
         * @param {Object} [options]
         * @param {boolean} [options.shuffle=false] - Whether to shuffle the working set
         * @param {boolean} [options.onlyUnknown=false] - Restrict the set to cards previously rated 'dontKnow'
         */
        startStudyMode({ state, commit }, { shuffle = false, onlyUnknown = false } = {}) {
            let cards = [...state.flashcards];
            if (onlyUnknown) {
                const previousRatings = state.flashcardStudyMode.ratings;
                cards = cards.filter(card => previousRatings[card.id] === 'dontKnow');
            }
            if (cards.length === 0) return;
            if (shuffle) {
                for (let i = cards.length - 1; i > 0; i--) {
                    const swapIndex = Math.floor(Math.random() * (i + 1));
                    [cards[i], cards[swapIndex]] = [cards[swapIndex], cards[i]];
                }
            }
            commit('setFlashcardStudyMode', { active: true, cards, currentIndex: 0, isFlipped: false, ratings: {} });
        },

        /**
         * Records the per-session self-assessment for a card. Not persisted to the backend.
         * @param {Object} payload
         * @param {string} payload.id - Flashcard id
         * @param {'know'|'dontKnow'} payload.rating
         */
        rateFlashcard({ state, commit }, { id, rating }) {
            commit('setFlashcardStudyMode', {
                ratings: { ...state.flashcardStudyMode.ratings, [id]: rating },
            });
        },

        /**
         * Closes the study session and returns to the flashcard list.
         */
        endStudyMode({ commit }) {
            commit('setFlashcardStudyMode', {
                active: false,
                cards: [],
                currentIndex: 0,
                isFlipped: false,
                isFullscreen: false,
                ratings: {},
            });
        },

        /**
         * Opens the plugin's panel, closing every other panel and the other
         * plugin's, or closes it when it is the one open.
         */
        togglePlugin({ commit, getters }, key) {
            const opening = !getters.isReaderPluginOpen(key);

            commit('closeAllSettingsOrFeatures');
            if (opening) commit('openReaderPlugin', key);
        },

        toggleThumbnails({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'thumbnails' });
            commit('toggleFeature', { featureKey: 'thumbnails' });
        },

        initializeThemeListener({ state, commit }) {
            state.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

            if (state.ui.theme === 'system') {
                commit('setThemeSelected', {
                    theme: state.systemThemeMediaQuery.matches ? 'dark' : 'light',
                });
            }

            state.handleSystemThemeChange = e => {
                if (state.ui.theme !== 'system') return;
                commit('setThemeSelected', { theme: e.matches ? 'dark' : 'light' });
            };

            const mediaQuery = state.systemThemeMediaQuery;

            if (typeof mediaQuery.addEventListener === 'function') {
                mediaQuery.addEventListener('change', state.handleSystemThemeChange);
            } else if (typeof mediaQuery.addListener === 'function') {
                /**
                 * Added support for older versions of iOS (<14)
                 * that do not support the 'addEventListener' method for MediaQueryList.
                 */
                mediaQuery.addListener(state.handleSystemThemeChange);
            } else {
                console.warn('MediaQueryList is not supported for this browser.');
            }
        },

        async addAndSaveHighlighter({ commit, dispatch }, payload) {
            commit('addHighlighter', payload);
            await dispatch('updateUserSettings');
        },

        async removeAndSaveHighlighter({ commit, state }, slug) {
            if (state.anonymousSession) {
                commit('removeHighlighter', slug);
                state.userSettings.customColors = state.customColors;
                return;
            }

            const isRemovingActiveQuickHighlightColor = state.quickHighlight.activeColorSlug === slug;
            const filteredCustomColors = state.customColors.filter(c => c.slug !== slug);
            const candidateSettings = {
                ...state.userSettings,
                customColors: filteredCustomColors,
            };

            if (isRemovingActiveQuickHighlightColor) {
                candidateSettings.quickHighlight = { ...state.quickHighlight, activeColorSlug: null };
            }

            await settingsAPI.update({ settings: candidateSettings });

            commit('removeHighlighter', slug);
            state.userSettings.customColors = state.customColors;
            if (isRemovingActiveQuickHighlightColor) {
                state.userSettings.quickHighlight = { ...state.quickHighlight };
            }
        },

        async updateAndSaveHighlighter({ commit, dispatch }, payload) {
            commit('updateHighlighter', payload);
            await dispatch('updateUserSettings');
        },

        /**
         * Enables/disables quick highlight mode and persists the change,
         * reverting the toggle and notifying the user when the save fails.
         * Toggles during an in-flight save are ignored — previousEnabled would capture an unpersisted optimistic value.
         * @param {boolean} enabled
         */
        async setQuickHighlightEnabledAndSave({ state, commit, dispatch }, enabled) {
            if (state.quickHighlightToggleSaveInFlight) return;
            const previousEnabled = state.quickHighlight.enabled;
            commit('setQuickHighlightEnabled', enabled);
            state.quickHighlightToggleSaveInFlight = true;
            try {
                await dispatch('updateUserSettings');
            } catch (error) {
                commit('setQuickHighlightEnabled', previousEnabled);
                // Re-mirror the just-reverted value so another save path can't persist the failed one.
                state.userSettings.quickHighlight = { ...state.quickHighlight };
                reportSettingsSaveFailure({ state, commit }, error);
            } finally {
                state.quickHighlightToggleSaveInFlight = false;
            }
        },

        /**
         * Sets the active quick highlight color and persists the change,
         * reverting the selection and notifying the user when the save fails —
         * unless a newer selection already took over while the save was in flight.
         * @param {string} colorSlug
         */
        async setQuickHighlightColorAndSave({ state, commit, dispatch }, colorSlug) {
            // Skip while a toggle save is in flight: persisting the color would carry the
            // optimistic `enabled` and leave it saved even if the toggle save then fails.
            if (state.quickHighlightToggleSaveInFlight) return;
            const previousColorSlug = state.quickHighlight.activeColorSlug;
            commit('setQuickHighlightColor', colorSlug);
            try {
                await dispatch('updateUserSettings');
            } catch (error) {
                if (state.quickHighlight.activeColorSlug !== colorSlug) {
                    Sentry.captureException(error);
                    return;
                }
                commit('setQuickHighlightColor', previousColorSlug);
                state.userSettings.quickHighlight = { ...state.quickHighlight };
                reportSettingsSaveFailure({ state, commit }, error);
            }
        },

        /**
         * Remembers the color just applied to a highlight or note as the quick
         * highlight color, so activating quick highlight later starts from it. Skips
         * while quick highlight is enabled — its color is then user-owned, changed
         * only from the panel — and persists without ever flipping `enabled`.
         * @param {string} colorSlug - Palette slug of the color just applied
         */
        async rememberQuickHighlightColor({ state, dispatch }, colorSlug) {
            // Quick highlight is hidden for anonymous sessions, so there is nothing to remember.
            if (state.anonymousSession) return;
            if (state.quickHighlight.enabled) return;
            if (!colorSlug || colorSlug === state.quickHighlight.activeColorSlug) return;

            await dispatch('setQuickHighlightColorAndSave', colorSlug);
        },
    },
    mutations: {
        plaPublicationManifestVersion(state, payload) {
            state.plaPublicationManifestVersion = payload.plaPublicationManifestVersion;
        },
        setTenantId(state, payload) {
            state.tenantId = payload.tenantId;
        },
        contentEncryptionIvSeed(state, payload) {
            state.contentEncryption.ivSeed = {
                tenant_id: payload.tenant_id,
                issue_id: payload.issue_id,
            };
        },
        coniglioTrackToken(state, payload) {
            state.coniglio.track_token = payload.coniglio_track_token;
        },
        setCopyContentToClipboard(state, payload) {
            state.copyContentToClipboard.enabled = payload.enabled;
        },
        /**
         * Sets the print control configuration coming from the host.
         * @param {Object} state
         * @param {{ enabled?: boolean, printLimitPerJob?: number, lifetimeTotalPages?: number, printedPages?: number[], readerDelivery?: boolean }} payload
         */
        setPrintControlConfig(state, payload) {
            state.printControl = { ...state.printControl, ...payload };
        },
        /**
         * Replaces the print history list.
         * @param {Object} state
         * @param {Array<{ pageFrom: number, pageTo: number }>} history
         */
        setPrintHistory(state, history) {
            state.printControl.history = history;
        },
        /**
         * Prepends a printed range to the history (most recent first). Skips the
         * range if it is already listed, so re-printing an existing range does not
         * duplicate the entry (the backend history is deduplicated by range too).
         * @param {Object} state
         * @param {{ pageFrom: number, pageTo: number }} entry
         */
        addPrintHistoryEntry(state, entry) {
            const alreadyListed = state.printControl.history.some(
                range => range.pageFrom === entry.pageFrom && range.pageTo === entry.pageTo
            );
            if (alreadyListed) return;
            state.printControl.history = [entry, ...state.printControl.history];
        },
        /**
         * Adds the unique pages of a range to the printed-pages set and keeps it sorted.
         * Mirrors locally what the backend counts as unique pages (optimistic update).
         * @param {Object} state
         * @param {{ pageFrom: number, pageTo: number }} range
         */
        addPrintedPages(state, { pageFrom, pageTo }) {
            const printedSet = new Set(state.printControl.printedPages);
            for (let page = pageFrom; page <= pageTo; page += 1) {
                printedSet.add(page);
            }
            state.printControl.printedPages = Array.from(printedSet).sort((a, b) => a - b);
        },
        loadingProcessIsFinished(state) {
            if (state.isLoadingProcessFinished) throw 'LoadingProcess should only be done once';
            state.isLoadingProcessFinished = true;
        },
        embeddedMode(state, payload) {
            state.embeddedMode = payload.embeddedMode;
        },
        previewMode(state, payload) {
            state.previewMode = payload.previewMode;
        },
        customPreview(state, payload) {
            state.customPreview = payload.customPreview;
        },
        setFileType(state, payload) {
            state.fileType = payload.fileType;
        },
        setEpubFilePath(state, payload) {
            state.epub.filePath = payload.filePath;
        },
        inIframe(state, payload) {
            state.inIframe = payload.inIframe;
        },
        setIssueId(state, payload) {
            state.issue.id = payload.issue_id;
        },
        polpoSessionHasBeenInitiated(state) {
            state.polpoSessionInitiated = true;
        },
        setReaderToken(state, payload) {
            state.readerToken = payload.token;
        },
        anonymousSession(state, payload) {
            state.anonymousSession = payload.anonymous_session;
        },
        setDeviceDetails(state, payload) {
            state.device.width = payload.width;
            state.device.height = payload.height;
            state.device.orientation = payload.orientation;
        },
        parentUrl(state, payload) {
            state.parentUrl = payload.url;
        },
        markInitialContentReady(state) {
            state.isInitialContentReady = true;
        },
        setLoadingState(state, payload) {
            state.isLoading = payload.isLoading;
            state.loadingSpinnerPosition = payload.position;
        },
        setTrackPlayerState(state, payload) {
            state.audiobook.trackPlayer.isLoading = payload.isLoading;
            state.audiobook.trackPlayer.isPlaying = payload.isPlaying;
        },
        setCurrentTrackDurationInSecondsState(state, payload) {
            state.audiobook.currentTrackDurationInSeconds = payload.durationInSeconds;
        },
        lang(state, payload) {
            state.lang = payload.lang;
            // Update document lang attribute for accessibility
            if (payload.lang) {
                document.documentElement.lang = payload.lang;
            }
        },
        /**
         * Merges an on-demand language's translations into the localizations map.
         * Languages other than the eagerly-bundled English fallback are loaded
         * lazily (see _shared_/lang/localizations.js) and committed here. The map
         * is reassigned rather than mutated so Vue 2 reactivity reaches the ~407
         * `localizations[lang]` accesses across the app.
         */
        setLocalization(state, { lang, messages }) {
            state.localizations = { ...state.localizations, [lang]: messages };
        },
        filesUrls(state, payload) {
            state.publication.files_urls = payload.files_urls;
        },
        filesInfo(state, payload) {
            state.publication.files_info = payload.files_info;
        },
        url(state, payload) {
            state.publication.url = payload.url;
        },
        tableOfContents(state, payload) {
            state.publication.table_of_contents = payload.table_of_contents;
        },
        title(state, payload) {
            state.title = payload.title;
        },
        /**
         * Updates the browser's document.title for accessibility.
         * @param {Object} payload - The payload object
         * @param {string} [payload.context] - Context-specific part (chapter title, page number, track name)
         */
        updateDocumentTitle(state, payload = {}) {
            const bookTitle = state.title || 'Reader';
            const context = payload.context;

            document.title = context ? `${bookTitle} - ${context}` : bookTitle;
        },
        issueTitle(state, payload) {
            state.issue.title = payload.title;
        },
        tags(state, payload) {
            state.issue.tags = payload.tags;
        },
        issueUrl(state, payload) {
            state.issue.issue_url = payload.issue_url;
        },
        shareUrl(state, payload) {
            state.issue.share_url = payload.share_url;
        },
        setIssueLang(state, payload) {
            state.issue.lang = payload.lang;
        },
        publicationDate(state, payload) {
            state.issue.publication_date = payload.publication_date;
        },
        publicationPlace(state, payload) {
            state.issue.publicationPlace = payload.publicationPlace;
        },
        publicationCover(state, payload) {
            state.publication.cover = payload.cover;
        },
        publicationAbout(state, payload) {
            state.issue.aboutThisPublication = payload.publication_about;
        },
        descriptionRawText(state, payload) {
            state.issue.descriptionRawText = payload.description_raw_text;
        },
        externalId(state, payload) {
            state.issue.externalId = payload.external_id;
        },

        // UI behaviour
        setStateUiHidden(state, uiHidden) {
            state.uiHidden = uiHidden;
        },

        openSidebar(state) {
            state.sidebar.opened = true;
            if (state.ui.selectionTool.opened) {
                state.ui.selectionTool.opened = false;
            }
        },
        closeSidebar(state) {
            state.sidebar.opened = false;
        },
        setTotalPages(state, payload) {
            state.reader.pagination.total = payload.total;
            state.reader.pagination.ready = true;
        },
        setEpubTotalPositions(state, payload) {
            state.epub.totalPositions = payload.total;
        },
        setEpubCurrentPosition(state, payload) {
            state.epub.currentPosition = payload.position;
        },
        setFixedLayoutPrintedPages(state, payload) {
            state.epub.fixedLayoutPrintedPages = payload.pages;
        },
        /**
         * The arrow keys stay paused while any feature holds them, so a feature
         * resuming them gives back its own hold and leaves the others in place.
         * @param {Object} state
         * @param {string} holder - The feature taking the keys
         */
        pauseKeyboardNavigation(state, holder) {
            if (!state.reader.keyboardNavigationHolders.includes(holder)) {
                state.reader.keyboardNavigationHolders.push(holder);
            }
            state.reader.keyboardNavigationEnabled = false;
        },
        /**
         * @param {Object} state
         * @param {string} holder - The feature giving the keys back
         */
        resumeKeyboardNavigation(state, holder) {
            const holders = state.reader.keyboardNavigationHolders.filter(other => other !== holder);
            state.reader.keyboardNavigationHolders = holders;
            state.reader.keyboardNavigationEnabled = holders.length === 0;
        },
        setPage(state, payload) {
            // Always resync the optimistic UI page to the committed one. This is
            // what clears the pending-jump loading spinner (Reader.vue compares
            // ui.pagination.current !== currentPage): a range-bar jump sets a
            // pending value via rangePageSelect, and landing here must clear it
            // even when the committed page is unchanged (e.g. an EPUB jump that
            // stays on the same printed page but a different Readium position).
            state.ui.pagination.current = payload.page;
            if (state.reader.pagination.current === payload.page) {
                return;
            }
            state.reader.pagination.current = payload.page;
        },
        setCurrentPageBookmark(state, payload) {
            state.reader.currentPageBookmarkUuid = payload?.uuid ?? null;
        },
        closeSelectionTools(state) {
            if (state.ui.selectionTool.opened) state.ui.selectionTool.opened = false;
        },
        //ui
        uiOpenSidebar(state) {
            state.ui.sidebar.opened = !state.ui.sidebar.opened;
            if (state.ui.selectionTool.opened && state.ui.sidebar.opened) {
                state.ui.selectionTool.opened = false;
            }
        },
        /**
         * Sets the user-resized width (px) of the left sidebar panel; pass null to reset to default.
         */
        setSidebarLeftWidth(state, width) {
            state.ui.sidebar.leftWidth = width;
        },
        /**
         * Flags whether the left panel is being actively drag-resized.
         */
        setSidebarResizing(state, isResizing) {
            state.ui.sidebar.resizing = isResizing;
        },

        /// Open and close features logic
        closeAllSettingsOrFeatures(state, { featureKey = null } = {}) {
            // A caller that has to keep more than one feature open names them in a list. The
            // read-aloud player plays beside the panels opened after it, so only its own close
            // stops it; starting it closes them like any other opener.
            const kept = [featureKey, 'listening'].flat();
            Object.keys(state.ui)
                .filter(key => !kept.includes(key))
                .forEach(key => {
                    if (!state.ui[key].opened) return;
                    state.ui[key].opened = false;
                });
            Object.keys(state.ui.features)
                .filter(key => !kept.includes(key))
                .forEach(key => {
                    if (!state.ui.features[key].opened) return;
                    state.ui.features[key].opened = false;
                });

            Object.keys(state.ui.audiobook).forEach(key => {
                if (!state.ui.audiobook[key].opened) return;
                state.ui.audiobook[key].opened = false;
            });
        },
        toggleFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui.features[featureKey][subFeature].opened = !state.ui.features[featureKey][subFeature].opened;
            } else {
                state.ui[featureKey].opened = !state.ui[featureKey].opened;
            }
        },
        openFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui[featureKey][subFeature].opened = true;
            } else {
                state.ui[featureKey].opened = true;
            }
        },
        closeFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui[featureKey][subFeature].opened = false;
            } else {
                state.ui[featureKey].opened = false;
            }
        },
        /**
         * Sets (or clears with null) the flashcard the panel should scroll to.
         * @param {Object} state
         * @param {string|null} uuid - The target flashcard uuid, or null to clear
         */
        setFlashcardFocusUuid(state, uuid) {
            state.flashcardFocusUuid = uuid;
        },

        /**
         * Sets (or clears with null) the note the notebook should scroll to.
         * @param {Object} state
         * @param {string|null} uuid - The target note uuid, or null to clear
         */
        setNoteFocusUuid(state, uuid) {
            state.noteFocusUuid = uuid;
        },

        rangePageSelect(state, payload) {
            state.ui.pagination.current = payload.page;
        },

        requestQueryWordsHighlight(state, payload) {
            state.search.requestQueryWordsHighlight = true;
            // The text layers read `queryWords.length` on every highlight request,
            // so a host result that carries no words must still store a list.
            state.search.queryWords = payload.queryWords ?? [];
            state.search.page = payload.page;
        },
        requestClearWordsHighlight(state, payload) {
            state.search.clear = payload;
        },
        fulfillQueryWordsHighlight(state) {
            state.search.requestQueryWordsHighlight = false;
            state.search.page = null;
            state.search.queryWords = [];
        },

        ...addOnsMutations,
        ...addOnBatchMutations,
        ...toursMutations,
        zoomByType(state, payload) {
            state.ui.settings.zoom.select.isSelected = payload.type;
        },
        setZoomScale(state, payload) {
            state.ui.settings.zoom.select.scale = payload.scale;
        },

        setEnforceConcurrencyLimit(state, payload) {
            state.concurrencyLimit.enforce = payload;
        },
        setDebugEnforceConcurrencyLimit(state, payload) {
            state.concurrencyLimit.debug = payload;
        },

        // #tts
        setTextToListen(state, payload) {
            if (payload.listenPage) {
                state.ui.listening.pageToListen = payload.text;
                if (state.ui.listening.opened) return;
            }
            state.ui.listening.texToListen = payload.text;
        },
        // #selections tool
        fetchedSelections(state, _payload) {
            state.selectionsHaveBeenFetched = true;
        },
        fetchedBookmarks(state) {
            state.bookmarksHaveBeenFetched = true;
        },
        toggleSelectionTool(state) {
            state.ui.selectionTool.opened = !state.ui.selectionTool.opened;
        },
        showSelectionTool(state, payload) {
            // Every viewer answers a selection by offering these tools, and
            // while the add-on form is open the selection is the draft's
            // anchor instead. Refusing here rather than in each viewer also
            // keeps the flag honest for everything else that reads it.
            if (state.ui.addOnsPanel.editor) return;

            state.ui.selectionTool.opened = true;
            state.ui.selectionTool.page = payload;
        },
        hideSelectionTool(state) {
            state.ui.selectionTool.opened = false;
        },
        setCurrentHighlight(state, payload) {
            state.currentHighlight = payload;
        },
        setHighlightModel(state, payload) {
            state.highlightModel = payload;
        },
        setHighlights(state, payload) {
            state.selections = payload.highlights;
        },
        setBookmarks(state, payload) {
            state.bookmarks = (payload.bookmarks || []).map(bookmark => ({
                ...bookmarkModel,
                id: bookmark.id,
                uuid: bookmark.uuid,
                type: bookmark.location?.type,
                page: bookmark.location?.page,
                locator: bookmark.location?.locator ?? null,
            }));
        },
        // A miss is a silent no-op, as in removeHighlighter: findIndex answers -1 and
        // splice(-1, 1) would drop the last entry instead of the one asked for.
        removeSelection(state, payload) {
            const index = findIndex(state.selections, ['uuid', payload.uuid]);
            if (index === -1) return;
            state.selections.splice(index, 1);
        },
        removeBookmark(state, payload) {
            state.bookmarks = state.bookmarks.filter(bookmark => bookmark.uuid !== payload.uuid);
        },
        setSelectionToolPosition(state, payload) {
            state.ui.selectionTool.position = payload;
        },
        setSelectionAnchor(state, payload) {
            state.ui.selectionTool.selectionTop = payload.selectionTop;
            state.ui.selectionTool.selectionLeft = payload.selectionLeft || 0;
        },
        pushSelection(state, payload) {
            state.selections.push(payload);
        },
        updateSelection(state, payload) {
            const index = findIndex(state.selections, ['uuid', payload.uuid]);
            // Same guard: splice(-1, 1, patch) would overwrite the last entry with a copy of
            // itself merged with the patch, corrupting an unrelated highlight.
            if (index === -1) return;
            state.selections.splice(index, 1, { ...state.selections[index], ...payload });
        },

        setFlashcardModel(state, payload) {
            state.flashcardModel = payload;
        },
        setFlashcardsFetched(state) {
            state.flashcardsHaveBeenFetched = true;
        },
        setFlashcards(state, payload) {
            state.flashcards = payload.flashcards;
            state.flashcardsHaveBeenFetched = true;
        },
        openFlashcardModal(state) {
            state.ui.flashcardModal.opened = true;
        },
        closeFlashcardModal(state) {
            state.ui.flashcardModal.opened = false;
        },
        setFlashcardsEnabled(state, payload) {
            state.ui.features.flashcards.enabled = payload.flashcards_enabled;
        },

        /**
         * Merges a partial payload into the study session state, preserving untouched fields.
         */
        openFlashcardForm(state, { card = null } = {}) {
            state.ui.flashcardsPanel.form = { card };
        },
        closeFlashcardForm(state) {
            state.ui.flashcardsPanel.form = null;
        },
        setFlashcardStudyMode(state, payload) {
            state.flashcardStudyMode = { ...state.flashcardStudyMode, ...payload };
        },
        toggleStudyFullscreen(state) {
            state.flashcardStudyMode = {
                ...state.flashcardStudyMode,
                isFullscreen: !state.flashcardStudyMode.isFullscreen,
            };
        },
        flipFlashcard(state) {
            state.flashcardStudyMode = {
                ...state.flashcardStudyMode,
                isFlipped: !state.flashcardStudyMode.isFlipped,
            };
        },
        nextStudyCard(state) {
            if (state.flashcardStudyMode.currentIndex < state.flashcardStudyMode.cards.length - 1) {
                state.flashcardStudyMode = {
                    ...state.flashcardStudyMode,
                    currentIndex: state.flashcardStudyMode.currentIndex + 1,
                    isFlipped: false,
                };
            }
        },
        prevStudyCard(state) {
            if (state.flashcardStudyMode.currentIndex > 0) {
                state.flashcardStudyMode = {
                    ...state.flashcardStudyMode,
                    currentIndex: state.flashcardStudyMode.currentIndex - 1,
                    isFlipped: false,
                };
            }
        },

        StandAloneAllFeatures(state) {
            state.ui.features.bookmarks.enabled = true;
            state.ui.features.flashcards.enabled = true;
            state.ui.search.enabled = true;
            state.ui.settings.enabled = true;
            state.ui.listening.enabled = true;
        },
        toggleIntegrationTool(state, payload) {
            state.integrationTool.opened = payload.opened;
            state.integrationTool.name = payload.name;
            state.integrationTool.text = payload.text;
            state.integrationTool.action = payload.action;
        },
        setAiIntegrationsEnabledOrDisabled(state, payload) {
            state.AiIntegrations.enabled = payload.enabled;
        },
        setFakeIssue(state, payload) {
            state.issue = payload.issue;
        },
        /**
         * Shows the toast, replacing the one on screen. Beyond its text, a toast
         * can carry an icon, a wait bar for work still running, and one action:
         * a link the user opens in a new tab (`href`) or a store action the
         * toast dispatches (`dispatch` with its `payload`). A persistent toast
         * stays until the user closes it or another toast replaces it.
         * @param {Object} state
         * @param {{ text: string, durationMs?: number, icon?: string, progress?: boolean, persistent?: boolean, action?: { label: string, href?: string, dispatch?: string, payload?: * } }} payload
         */
        openToast(state, payload) {
            state.toast = { ...TOAST_DEFAULTS, ...payload, opened: true };
        },
        closeToast(state) {
            state.toast = { ...TOAST_DEFAULTS, opened: false };
        },
        openShareCollectionModal(state) {
            state.ui.shareCollectionModal.opened = true;
        },
        closeShareCollectionModal(state) {
            state.ui.shareCollectionModal.opened = false;
        },
        openOnboarding(state) {
            state.onboarding.hasToBeShown = true;
        },
        closeOnboarding(state) {
            state.onboarding.hasToBeShown = false;
        },
        openTourBanner(state) {
            state.tourBanner.opened = true;
        },
        closeTourBanner(state) {
            state.tourBanner.opened = false;
        },
        openPromoteApp(state) {
            state.promoteApp.hasToBeShown = true;
        },
        closePromoteApp(state) {
            state.promoteApp.hasToBeShown = false;
        },
        // #tts
        loadPageTextToTTS(state) {
            state.ui.listening.texToListen = state.ui.listening.pageToListen;
        },
        openListeningPlayer(state) {
            state.ui.listening.opened = true;
        },
        closeListeningPlayer(state) {
            state.ui.listening.opened = false;
        },
        setListeningEnabled(state, payload) {
            state.ui.listening.enabled = payload.tts_enabled;
        },
        setExtraVoices(state, payload) {
            state.ui.extraVoices.chinese = payload.chinese;
        },
        setListeningDriver(state, payload) {
            state.ui.listening.driver = payload.tts_driver;
        },
        setSearchEnabled(state, payload) {
            state.ui.search.enabled = payload.search_enabled;
        },
        setReaderLayout(state, layout) {
            /**
             * This layout information is using to update the component type for rendering single or double view in PDF
             * and affect when is change in EPUBs because re-render vue component.
             * For epubs you must to use `setEpubLayoutMode`
             */
            state.reader.layout = layout;
        },

        /**
         * UI
         */
        setHeaderbarOffsetHeight(state, payload) {
            state.ui.headerbar.offsetHeight = payload;
        },
        setFooterbarOffsetHeight(state, payload) {
            state.ui.toolbar.offsetHeight = payload;
        },
        setFooterbarShownHeight(state, payload) {
            state.ui.toolbar.shownHeight = payload;
        },
        /**
         * Scimia EPUB
         */
        filePath(state, payload) {
            state.epub.filePath = payload.filePath;
        },
        setReadium(state, payload) {
            state.readium = payload.readium;
            if (payload.epubViewer) {
                state.epubViewer = payload.epubViewer;
            }
        },
        setEpubViewer(state, payload) {
            state.epubViewer = payload.epubViewer;
        },
        fontSizeUpdate(state, payload) {
            state.settings.fontSize = payload.fontSize;
        },
        setEpubLayoutType(state, payload) {
            state.isFixedLayout = payload.type;
        },
        setFixedLayoutRulesOutSpreads(state, rulesOutSpreads) {
            state.fixedLayoutRulesOutSpreads = rulesOutSpreads;
        },
        setEpubLayoutMode(state, payload) {
            state.settings.scroll = payload.scroll;
            state.settings.syntheticSpread = payload.syntheticSpread;
        },
        /**
         * Dev-only: set the EPUB layout (single | scroll | double) from a dev
         * query param. EpubViewer reads userSettings.epub.layout directly at
         * mount, so we must update both that and the active scroll/spread
         * settings via the epubLayouts map.
         */
        setDevEpubLayout(state, layout) {
            const mode = state.epubLayouts[layout] ? layout : 'single';
            state.userSettings.epub.layout = mode;
            state.settings.scroll = state.epubLayouts[mode].scroll;
            state.settings.syntheticSpread = state.epubLayouts[mode].syntheticSpread;
        },
        setDirection(state, payload) {
            state.epub.direction = payload.direction;
        },
        setFarfallaToc(state, payload) {
            state.epub.farfallaToc = Array.isArray(payload) ? payload : [];
        },
        setEffectiveToc(state, payload) {
            state.publication.table_of_contents = Array.isArray(payload) ? payload : [];
            state.epub.currentTocLocation = createEmptyEpubTocLocation();
        },
        setCurrentTocLocation(state, payload) {
            const location = payload || createEmptyEpubTocLocation();
            if (epubTocLocationsEqual(state.epub.currentTocLocation, location)) return;

            state.epub.currentTocLocation = {
                path: Array.isArray(location.path) ? [...location.path] : null,
                ancestorPaths: (location.ancestorPaths || []).map(path => [...path]),
                confidence: location.confidence || 'none',
                source: location.source || null,
            };
        },
        setHasLoadLastLocation(state) {
            state.hasLoadLastLocation = true;
        },
        setSettingsChange(state, payload) {
            state.settingsChange = payload.status;
        },
        setDoNotTrackFontLoc(state, payload) {
            state.doNotTrackFontLoc = payload.status;
        },
        setContext(state, payload) {
            state.context.push(payload);
        },
        cleanContext(state) {
            state.context = [];
        },
        setUiTheme(state, payload) {
            const theme = payload.theme ? payload.theme : 'light';
            state.ui.theme = theme;
            // Update the themeSelected only if the theme is not 'system'. The ui is listening themeSelected property
            if (theme !== 'system') {
                state.ui.themeSelected = theme;
            }
        },
        setBaseTheme(state, payload) {
            state.ui.baseTheme = payload.theme;
        },
        setThemeSelected(state, payload) {
            state.ui.themeSelected = payload.theme;
        },

        setSettings(state, settings) {
            state.reader.preview = settings.preview;
            state.reader.layout = settings.layout || 'scroll';
            if (settings.pageViewConfirmDelayMs != null) {
                state.reader.pageViewConfirmDelayMs = settings.pageViewConfirmDelayMs;
            }
            state.reader.socialShareDisabled = settings.socialShareDisabled === true;
        },
        setNetworkStatus(state, payload) {
            state.isOffline = payload.isOffline || false;
        },

        //AudioBook
        toggleAudiobookAbout(state) {
            state.ui.audiobook.about.opened = !state.ui.audiobook.about.opened;
        },
        toogleAudiobookPlaylist(state) {
            state.ui.audiobook.playlist.opened = !state.ui.audiobook.playlist.opened;
        },
        loadingAudiobookPlaylist(state, payload) {
            state.audiobook.playlist = payload.playlist;
            state.audiobook.duration = payload.duration;
            state.audiobook.chapters = payload.chapters;
        },
        changeAudiobookSpeed(state, payload) {
            state.audiobook.speed = payload.speed;
        },
        setTwitterUser(state, payload) {
            state.issue.twitterUser = payload.twitterUser;
        },
        setHasPagebreaks(state, payload) {
            state.issue.hasPagebreaks = !!payload.hasPagebreaks;
        },
        openSocialShare(state, payload) {
            if (state.reader.socialShareDisabled) {
                return;
            }
            state.ui.socialShare.opened = true;
            if (payload) {
                state.ui.socialShare.shareArticle = payload.shareArticle ? payload.shareArticle : false;
                state.ui.socialShare.currentArticleId = payload.currentArticleId ? payload.currentArticleId : null;
            }
        },
        closeSocialShare(state) {
            state.ui.socialShare.opened = false;
            state.ui.socialShare.shareArticle = false;
            state.ui.socialShare.currentArticleId = null;
        },
        openNoteEditor(state, payload) {
            state.ui.noteEditor.opened = true;
            state.ui.noteEditor.uuid = payload?.uuid ?? null;
        },
        closeNoteEditor(state) {
            state.ui.noteEditor.opened = false;
            state.ui.noteEditor.uuid = null;
        },
        openConfirmationModal(state, payload) {
            state.ui.confirmationModal.opened = true;
            state.ui.confirmationModal.title = payload.title || '';
            state.ui.confirmationModal.message = payload.message || '';
            state.ui.confirmationModal.confirmLabel = payload.confirmLabel || '';
            state.ui.confirmationModal.cancelLabel = payload.cancelLabel || '';
            state.ui.confirmationModal.onConfirmAction = payload.onConfirmAction || null;
            state.ui.confirmationModal.context = payload.context || {};
            state.ui.confirmationModal.destructive = payload.destructive ?? true;
        },
        closeConfirmationModal(state) {
            state.ui.confirmationModal.opened = false;
            state.ui.confirmationModal.title = '';
            state.ui.confirmationModal.message = '';
            state.ui.confirmationModal.confirmLabel = '';
            state.ui.confirmationModal.cancelLabel = '';
            state.ui.confirmationModal.onConfirmAction = null;
            state.ui.confirmationModal.context = {};
            state.ui.confirmationModal.destructive = true;
        },
        setUuidToListen(state, payload) {
            if (payload && payload.uuidToListen) {
                state.ui.listening.uuidToListen = payload.uuidToListen;
            } else {
                state.ui.listening.uuidToListen = '';
            }
        },
        setNotesEnabled(state, payload) {
            state.ui.features.notes.enabled = payload.notes_enabled;
        },
        setBookmarksEnabled(state, payload) {
            state.ui.features.bookmarks.enabled = Boolean(payload.bookmarks_enabled);
        },
        setRichTextEditorEnabled(state, payload) {
            state.ui.features.richTextEditor.enabled = Boolean(payload.rich_text_editor_enabled);
        },
        setRichTextEmbedHosts(state, payload) {
            state.ui.features.richTextEditor.embedHosts = {
                all: payload.embed_hosts?.all ?? [],
                video: payload.embed_hosts?.video ?? [],
                // Absent from a host that predates audio embeds, which is the
                // reason for the fallback: the editor then offers video only,
                // exactly as it did before.
                audio: payload.embed_hosts?.audio ?? [],
            };
        },
        setFullscreenEnabled(state, payload) {
            state.ui.features.fullscreen.enabled = Boolean(payload.fullscreen_enabled);
        },
        setFullscreenState(state, isFullscreen) {
            state.isFullscreen = Boolean(isFullscreen);
        },
        /**
         * The host's enabled plugins, in its order (Farfalla puts Vito
         * first). A host that predates the list sends only the single src,
         * so that plugin's id and name stay unknown. The key tells the
         * plugins apart in the panel state and the DOM, for an unknown id
         * too, so a second entry with the same key is dropped.
         */
        setReaderPlugins(state, payload) {
            const plugins = Array.isArray(payload.plugins)
                ? payload.plugins
                : [{ id: null, name: null, src: payload.src }];

            state.ui.plugin.plugins = plugins
                .filter(plugin => plugin?.src)
                .map(plugin => ({
                    key: plugin.id ?? 'plugin',
                    id: plugin.id ?? null,
                    name: plugin.name ?? null,
                    src: plugin.src,
                }))
                .filter((plugin, index, plugins) => plugins.findIndex(other => other.key === plugin.key) === index);
        },
        openReaderPlugin(state, key) {
            state.ui.plugin.activeKey = key;
            state.ui.plugin.opened = true;
        },
        setIsBuyable(state, payload) {
            state.isBuyable = payload.isBuyable;
        },
        setUserIsGuest(state, payload) {
            state.userIsGuest = payload.userIsGuest;
        },
        setIsBuyableButtonText(state, payload) {
            state.isBuyableButtonText = payload.isBuyableButtonText;
        },
        setPromoteAppData(state, payload) {
            state.promoteApp.enabled = payload?.enabled || false;
            state.promoteApp.androidAppLink = payload?.android_app_link;
            state.promoteApp.appleAppLink = payload?.apple_app_link;
            state.promoteApp.appName = payload?.app_name;
            state.promoteApp.appIcon = payload?.app_icon;
        },
        setSpineAndTotalWords(state, payload) {
            state.epub.spine = payload.spine;
            state.epub.totalWords = payload.totalWords;
        },
        setSubSpine(state, payload) {
            state.epub.subSpine = payload.subSpine;
        },
        setExhibitionMode(state, payload) {
            if (!payload) return;
            state.exhibitionMode = payload.exhibitionMode;
        },
        toggleShowAddToLists(state) {
            state.addToLists.show = !state.addToLists.show;
        },
        setSessionExpired(state) {
            state.sessionExpired = true;
        },
        setComponentListsIframeUrl(state, payload) {
            state.addToLists.iframeUrl = payload.iframeUrl;
        },
        aiActions(state, payload) {
            state.aiActions = payload;
        },
        setSelectionInProgress(state, payload) {
            state.selectionInProgress = payload.selectionInProgress;
        },
        setFirstPageReached(state, payload) {
            state.reader.pagination.isFirstPage = payload;
        },
        setLastPageReached(state, payload) {
            state.reader.pagination.isLastPage = payload;
        },

        // Custom colors mutations
        setCustomColors(state, colors) {
            const colorTemp = Array.isArray(colors) ? colors : [];
            if (colorTemp.length > MAX_CUSTOM_TAGS)
                Sentry.captureMessage(`setCustomColors truncated ${colorTemp.length} → ${MAX_CUSTOM_TAGS}`, 'error');
            state.customColors = colorTemp.slice(0, MAX_CUSTOM_TAGS);
        },

        setSharedColors(state, colors) {
            state.sharedColors = Array.isArray(colors) ? colors.map(color => ({ ...color, custom: true })) : [];
        },

        addHighlighter(state, payload) {
            if (state.customColors.length >= MAX_CUSTOM_TAGS) return;
            state.customColors.push({
                slug: payload.slug,
                label: payload.label,
                color: payload.color,
            });
        },

        removeHighlighter(state, slug) {
            const index = state.customColors.findIndex(c => c.slug === slug);
            if (index !== -1) {
                state.customColors.splice(index, 1);
            }
            if (state.quickHighlight.activeColorSlug === slug) {
                state.quickHighlight.activeColorSlug = null;
            }
        },

        updateHighlighter(state, { slug, label }) {
            const customColor = state.customColors.find(c => c.slug === slug);
            if (customColor && label !== undefined) {
                customColor.label = label;
            }
        },

        // Quick highlight mutations
        /**
         * Sets whether quick highlight mode is active.
         * @param {boolean} enabled
         */
        setQuickHighlightEnabled(state, enabled) {
            state.quickHighlight.enabled = enabled;
        },

        /**
         * Sets the active quick highlight color; null falls back to the first built-in.
         * @param {string|null} colorSlug
         */
        setQuickHighlightColor(state, colorSlug) {
            state.quickHighlight.activeColorSlug = colorSlug;
        },
    },
    modules: {
        PdfPagedReaderScale,
    },
};
